import { Capacitor } from '@capacitor/core'
import { Loading, QSpinnerTail, Notify, Dialog, date } from 'quasar'
import moment from 'moment'
// Utils
import { resolveSavedImage } from '@/utils/image'
import utils from 'src/utils'
import { isTaskIncomplete } from 'src/utils/tasks'
import { getPhotoQualityProfile } from '@/utils/photoQuality'
import { PICTURE_ADDON_TYPES } from '@/utils/pictureAddonTypes'
import { organizationIdOf } from '@/utils/user'
import { withTimeout } from '@/utils/promise'
import { buildCommentsToResync, validateVideoFile } from '@/utils/addons'
import { pdfFilename } from '@/utils/fileName'
// Services
import OccurrencesService from 'src/services/occurrences'
import TasksService from '@/services/tasks'
import UsersService from 'src/services/users'
// Models
import Task from 'src/models/ActionPlanTask.model'
import Form from 'src/models/Form.model'
// Stores
import { useOrganizationStore } from '@/stores/index.js'
// Services
import {
  saveOfflineImage as saveAnyOfflineAddon,
  readImageFromFileSystem,
  readImageAsBlob
} from 'src/services/offline-images'
import { OfflineStorage } from 'src/services/offline-storage'
import { dbg as offlineDbg, dbgWarn as offlineDbgWarn, dbgError as offlineDbgError } from 'src/offline/logger'
import { reportPhotoCaptureFailed } from 'src/offline/photos/photoIssues'
import { serializeExtraProperties } from 'src/offline/sync/builders/extra-properties'
import { isOfflineCaptureReady, isOfflineStorageReady } from '@/offline/capture-guard'
import { isReallyOnlineUi } from 'src/services/connectivity'
import { i18n } from 'src/boot/i18n'
// Capa offline (SQLite): fachada de dominio + nudge de sync. ESTA es la ÚNICA superficie offline
// que el store importa (frontend-standards: el store NO toca SQL/StorageAdapter). Addons y roles de
// firma se persisten/leen por la fachada
// (data+outbox / tabla meta) en vez del camino legacy; el estado en memoria se conserva para que la
// UI reaccione de inmediato. Offline-FIRST + nudge.
import { occurrences as offlineOccurrences, signatureRoles as offlineSignatureRoles, tasks as offlineTasks } from 'src/offline/domain'
import { syncNow as offlineSyncNow } from 'src/offline/sync/sync-worker'
// Nudge COALESCIDO del sync (TKT-2240): guardar un addon no dispara un drain propio; los guardados
// consecutivos comparten uno solo. El drain inmediato (`offlineSyncNow({ force: true })`) queda
// para el cierre y para el drenado previo al envio de correo, donde el usuario espera el resultado.
import { scheduleSyncNudge } from 'src/offline/sync/sync-nudge'
// Serializa los writes de addon por client_uuid: dos enqueueAddonV2 sobre la MISMA fila commitean en
// orden de llegada, invariante que la PK por client_uuid asume (TKT-2280). Ver serializeWrites.js.
import { createKeyedSerializer } from 'src/offline/serializeWrites'
// Cierre DIFERIDO (SQLite caida + sin red): no deja fila en el outbox, vive en OfflineStorage. Para
// decidir si el server ya tiene el cierre hay que mirarlo tambien (TKT-2182).
import { findDeferredClose, hasDeferredClose, replayPendingCloses, savePendingClose } from 'src/offline/pending-close'
import { listPendingSignatures, removePendingSignature, replayPendingSignatures, savePendingSignatures } from '@/offline/pending-signatures'
import { discardPendingComment, getPendingComment, replayPendingComments, savePendingComment } from '@/offline/pending-comments'
import { closeWithFallback } from '@/offline/finalize-close'
import { captureOfflineError, recordBreadcrumb } from '@/offline/telemetry'
import { newId, isServerId, taskClientUuid } from '@/offline/repositories/ids'
import {
  drainLegacyAnswers,
  writeLegacyAnswer,
  persistLegacyAddon,
  clearLegacyAddon,
  saveLegacyDocument,
  clearLegacyDocument,
  removeSyncedLegacyAddonRow,
  saveLegacyVideoBlob,
  upsertLegacyVideoRow,
  purgeLegacyVideo,
  findLegacyRawPhoto,
  listRealPendingLegacyAnswers,
  discardStaleLegacyAnswers
} from '@/offline/storage/legacy-answers'
// Predicado puro del aviso "auditoría sin finalizar": vive fuera del store para que el test
// ejercite el mismo código que corre en producción (TKT-2182).
import { shouldConfirmUnfinished } from 'src/offline/send-guard'

/**
 * Resuelve el catálogo de roles de firma adicional con cache offline (ISS-843).
 * Online: fetch al server + cachea la lista CRUDA en `meta` (offline). Offline o fallo:
 * lee del cache la lista keyed por los `signature_roles` pedidos. El flujo online normal
 * queda intacto (el cacheo es best-effort y nunca rompe el fetch).
 */
async function resolveSignatureRolesWithCache (payload) {
  // F9 (PR review): acotado a 4s — abre el dialogo de firma; timeout -> cae al cache offline (seguro).
  if (await isReallyOnlineUi()) {
    try {
      const data = await OccurrencesService.signaturesRoles(payload)
      // Cacheo best-effort: persiste la lista CRUDA (el endpoint no da `id`),
      // keyed por el payload. No debe romper el flujo online si SQLite falla.
      offlineSignatureRoles.cacheSignatureRoles(payload, data)
      return data
    } catch (err) {
      // Sin conectividad real (o el endpoint cayó): caemos al cache.
      console.error(err)
      return offlineSignatureRoles.getCachedSignatureRoles(payload)
    }
  }

  return offlineSignatureRoles.getCachedSignatureRoles(payload)
}

function buildOccurrenceFormData (payload) {
  const formData = new FormData()

  if (Array.isArray(payload.addons_values)) {
    payload.addons_values.forEach((addon, index) => {
      Object.keys(addon).forEach(key => {
        const value = addon[key]
        if (value === undefined || value === null) return

        if (key === 'file' && (value instanceof File || value instanceof Blob)) {
          formData.append(
            `addons_values[${index}][file]`,
            value,
            value.name || `image-${Date.now()}.jpg`
          )
        } else {
          formData.append(`addons_values[${index}][${key}]`, value)
        }
      })
    })
  }

  return formData
}

// Una sola cadena por client_uuid para todos los writes de addon del runtime (ver enqueueAddonV2).
const addonWriteSerializer = createKeyedSerializer()

// Última carga pedida por find(): la respuesta de una carga ya reemplazada no se aplica
let latestFindRequest = 0

// Pasamanos temporal (D-1b): Occurrence.vue importa waitForIndexedDBReady de aquí y su ruta definitiva
// la decide T4.1. La función vive en offline/storage/legacy-answers.js.
export { waitForIndexedDBReady } from '@/offline/storage/legacy-answers'

const APP_VERSION = import.meta.env.APP_VERSION || 'unknown'

// updateCopy responde addons como objeto por item_id con { type, answer }; save-offline-answers-copy
// responde un array plano de { item_id, type, value }. Se normaliza todo a la primera forma para que
// findConfirmedRemoteUrl trabaje igual con ambos endpoints.
function extractServerAddons (body) {
  const raw = body?.data?.addons || body?.addons || {}
  if (!Array.isArray(raw)) return raw

  const byItem = {}
  for (const entry of raw) {
    const itemId = String(entry?.item_id ?? '')
    if (!itemId) continue
    if (!byItem[itemId]) byItem[itemId] = []
    byItem[itemId].push({ type: entry.type, answer: entry.value })
  }

  return byItem
}

function isRemoteAnswerUrl (value, localRef) {
  if (typeof value !== 'string') return false

  const url = value.trim()
  if (!url) return false
  if (url.startsWith('blob:')) return false
  if (localRef && url === localRef) return false

  return true
}

function findConfirmedRemoteUrl (serverAddons, ans) {
  const list = serverAddons?.[ans.item_id] || serverAddons?.[String(ans.item_id)]
  if (!Array.isArray(list)) return null

  const match = list.find(a => a?.type === ans.type)
  const localRef = ans.answer_ref || ans.image_ref

  return isRemoteAnswerUrl(match?.answer, localRef) ? match.answer : null
}

const VIDEO_REJECTING_STATUSES = new Set([413, 422])

function getResponseStatus (error) {
  return error?.status ?? error?.response?.status ?? null
}

function notifyVideoUploadFailure (error) {
  const status = getResponseStatus(error)
  const key = VIDEO_REJECTING_STATUSES.has(status)
    ? 'forms.addons.video_rejected_by_server'
    : 'forms.addons.video_upload_retry'

  Notify.create({ type: 'negative', message: i18n.global.t(key) })
}


// TKT-1975 — lista de usuarios elegibles para la asignación inicial de tareas de plan de acción, por
// ocurrencia + rol + sucursal. Cada ítem reprobado con plan automático crea una tarea; sin esta caché
// serían N requests idénticos en la misma auditoría. Solo guarda respuestas buenas y se reinicia al
// cambiar de ocurrencia, así que su tamaño es el de los roles usados en una sola auditoría.
const ASSIGNEE_CANDIDATES_TIMEOUT_MS = 5000
const assigneeCandidatesCache = { occurrenceId: null, byKey: new Map() }

function checkBrokenImages(occurrence) {
  const broken = []

  Object.keys(occurrence.addons).forEach(itemId => {
    const list = occurrence.addons[itemId] || []

    list.forEach(addon => {
      const isPicture = addon.type && addon.type.includes('picture')
      if (!isPicture) return

      const invalidAnswer =
        addon.answer === null ||
        addon.answer === '' ||
        addon.answer === undefined

      const invalidThumbnail =
        addon.thumbnail === '' ||
        addon.thumbnail === null

      const invalidLocalPath =
        addon.localPath && typeof addon.localPath !== 'string'

      if (invalidAnswer || invalidThumbnail || invalidLocalPath) {
        broken.push({
          item_id: addon.item_id,
          type: addon.type
        })
      }
    })
  })

  return broken
}

// find()s solapados: si otra carga cambió la ocurrencia durante el await, NO forzar is_closed/score
// sobre la occ equivocada (la marcaría read-only con el score de otra ocurrencia).
async function markCurrentClosedPending (store, occurrenceId, readScore) {
  if (String(store.current?.id) !== String(occurrenceId)) return
  store.current.is_closed = true
  store.current._pendingLocalClose = true
  const pendingScore = await readScore()
  if (pendingScore != null && String(store.current?.id) === String(occurrenceId)) store.current.score = pendingScore
}

// Reenvía las firmas del respaldo (F-34) y les pone el id que devolvió el servidor.
async function sendPendingSignatures (current) {
  try {
    const { sent } = await replayPendingSignatures()
    sent.forEach(({ ref, serverId }) => {
      const sig = current?.signatures?.find(s => s._pendingSignatureRef === ref)
      if (sig && serverId != null) sig.id = serverId
    })
  } catch (err) {
    console.error(err)
  }
}

// Firma en el formato del PUT, sin las marcas locales (_pendingClientUuid, _pendingSignatureRef).
function signatureForServer (signature) {
  const plain = JSON.parse(JSON.stringify(signature))
  Object.keys(plain).filter(key => key.startsWith('_')).forEach(key => delete plain[key])
  return plain
}

function buildPreAnswers (preAnswers, { item, obtainedPoints, possiblePoints }) {
  const first = preAnswers?.[0]
  if (!first) return []

  const justification = first.ia_image_evaluation_justification !== undefined
    ? first.ia_image_evaluation_justification
    : null

  if (item.logic_properties && item.logic_properties.length === 0) {
    const currentPoints = obtainedPoints()
    return [{
      item_id: first.item_id,
      value: first.value,
      extra_properties: first.extra_properties,
      current_points: currentPoints !== 'na' ? currentPoints : 0,
      total_points: currentPoints !== 'na' ? possiblePoints() : 0,
      ia_image_evaluation_justification: justification
    }]
  }

  return [{
    item_id: first.item_id,
    value: first.value,
    extra_properties: first.extra_properties,
    current_points: 0,
    total_points: 0,
    ia_image_evaluation_justification: justification
  }]
}

async function resolvePhotoQualityProfile () {
  const { useOrganizationStore, useUsersStore } = await import('src/stores/index.js')
  return getPhotoQualityProfile({
    extraSettings: useOrganizationStore().getExtraSettings,
    organizationId: organizationIdOf(useUsersStore().current)
  })
}

async function currentUserId () {
  const { useUsersStore } = await import('src/stores/index.js')
  return useUsersStore().current.id
}

// Respuesta de la captura local pendiente superpuesta a la del server (overlayPendingOfflineAnswers).
// MERGE, no reemplazo: setCurrentOccurrenceAnswer pisa la answer entera, y la fila offline no
// guarda los campos que solo produce el server (p.ej. ia_image_evaluation_justification). Se
// parte de la del server y se superponen SOLO los campos de la captura local pendiente.
function mergePendingAnswer (serverAnswers, pending) {
  const server = serverAnswers.find(a => a.item_id === pending.item_id) || {}
  return {
    ...server,
    item_id: pending.item_id,
    value: pending.value,
    current_points: pending.current_points,
    total_points: pending.total_points,
    extra_properties: pending.extra_properties
  }
}

// Placeholder vacio de un addon de texto (comentario o documento): la UI lo muestra y la fila existe
// en SQLite. NO pisar uno con contenido: enqueue answer:'' ahora es un BORRADO real (gate de borrado
// activo vía app_version). El placeholder solo SIEMBRA la fila cuando no hay addon de ese tipo (o está
// vacío); sin este guard, "Agregar comentario/documento" (o el auto-disparo por item.comment_addon)
// sobre uno existente lo borraría en el server.
function seedEmptyTextAddon (store, itemId, type) {
  const existing = store.current?.addons?.[itemId]?.find(a => a.type === type)
  if (existing?.answer) return
  store.setAddons({
    answer: '',
    type,
    item_id: itemId
  })
  store.enqueueAddonV2({ item_id: itemId, type, answer: '' })
}

export default {
  async saveAndClose(payload) {
    // 1️⃣ Validar imágenes rotas antes de cerrar auditoría
    const broken = checkBrokenImages(this.current)

    if (broken.length > 0) {
      return new Promise(resolve => {
        Dialog.create({
          title: 'Fotos faltantes o dañadas',
          message: `Se detectaron ${broken.length} foto(s) que no se subieron correctamente.<br>
                    ¿Deseas volver a intentarlo?`,
          html: true,
          cancel: true,
          ok: { label: 'Reintentar', color: 'primary' }
        }).onOk(() => {
          broken.forEach(b => {
            const list = this.current.addons[b.item_id]
            const index = list.findIndex(a => a.type === b.type)
            if (index > -1) list.splice(index, 1)
          })

          Notify.create({
            type: 'warning',
            message: 'Corrige o vuelve a subir las fotos faltantes.'
          })

          this.logicFag = !this.logicFag

          resolve(false)
        }).onCancel(() => {
          Notify.create({
            type: 'negative',
            message: 'No se puede finalizar porque faltan fotos.'
          })
          resolve(false)
        })
      })
    }

    // 5️⃣ Si no hay fotos rotas, continuar flujo normal
    const promise = OccurrencesService.saveAndClose(payload)

    promise.then(() => {
      const notify = OccurrencesService.notify(payload.occurrence_id)
      notify.catch(err => console.error(err))
    })
    .catch(err => console.error('Error saving and closing occurrence:', err))

    return promise
  },
  // Devuelve una PROMESA que resuelve cuando this.current ya quedó asignado (getGroups es un http.get:
  // la asignación es asíncrona). Los callers existentes ignoran el retorno (aditivo); find() SÍ lo
  // encadena: el overlay offline derreferencia this.current, y lanzarlo antes de que exista lo hacía
  // morir con TypeError DENTRO de su catch best-effort -> se perdía el overlay ENTERO en silencio
  // (todas las fotos offline invisibles al reingresar, intermitente — carrera getGroups vs overlay).
  setCurrent (occurrence, isLatestRequest = () => true) {
    if (occurrence === null || occurrence === undefined) {
      this.current = null
      return Promise.resolve()
    }
    if (Object.keys(occurrence.addons).length === 0) occurrence.addons = {}

    occurrence.form.items.forEach(_item => {
      if (occurrence.addons[_item.id] === undefined) occurrence.addons[_item.id] = []
    })

    return Form.getGroups(occurrence.form.id)
      .then(res => {
        if (!isLatestRequest()) return
        occurrence.groups = res
        this.current = occurrence
      }).catch(err => {
        // Se conserva el swallow histórico (la vista muestra su propio error); current queda null y el
        // overlay de find() se vuelve un no-op seguro.
        console.error('ERROR', err)
      })
  },
  // Corta el token de find() de inmediato, sin esperar a que se invoque el siguiente find():
  // fetchOccurrence puede tardar hasta 4s en llegar ahí (espera a IndexedDB), ventana en la que
  // una carga vieja seguía pareciendo "la más reciente".
  invalidatePendingFind () {
    latestFindRequest++
  },
  find (id) {
    const request = ++latestFindRequest
    const isLatestRequest = () => request === latestFindRequest
    this.setCurrent(null)
    this.loading = true
    this.unansweredItems = []
    this.groupsWithUnansweredItems = new Set()
    // Se devuelve la CADENA (con su .catch), no la promesa original: el .then retorna `data` para que
    // el caller reciba el response igual. Robustez (ISS-845): si la carga falla, no dejar el spinner
    // infinito (loading=false) y avisar UNA vez. Para que fetchOccurrence NO duplique el aviso, el
    // catch re-lanza un error MARCADO (occurrenceLoadNotified) que el caller reconoce y no re-notifica.
    return OccurrencesService.find(id)
      .then(data => {
        if (!isLatestRequest()) return data
        if (data.data && (data.data.taskForm || data.data.taskEditing)) {
          if (data.data.taskForm) {
            this.tasksForm = data.data.taskForm.map(task => {
              return new Task(task)
            })
          }
          if (data.data.taskEditing) {
            this.tasksEditing = data.data.taskEditing.map(task => {
              return new Task(task)
            })
          }
        }
        if (data.data && data.data.addons.length === 0) data.data.addons = {}

        const currentReady = this.setCurrent(data.data, isLatestRequest) || Promise.resolve()
        this.loading = false
        // Overlay offline (SQLite v2): find() reconstruye this.current desde el SERVER y NO trae las
        // fotos/firmas capturadas offline que aún NO se subieron -> al reabrir la occurrence tras matar
        // la app quedaban INVISIBLES (siguen durables en SQLite, pero el usuario no las ve = "se
        // perdieron"). Se superponen las pendientes sobre el estado del server, en sus TRES formas:
        // addons-foto, answers-foto (item-foto) y firmas (ISS-994). Sin pendientes ⇒
        // no-op (flujo online intacto). Best-effort: un fallo NO rompe la carga (devolvemos `data` igual).
        // ENCADENADO a currentReady: setAddons/setCurrentOccurrenceAnswer/this.current.signatures escriben
        // en this.current, que setCurrent asigna asíncrono (tras getGroups). Correr el overlay antes lo
        // mataba con TypeError dentro de su catch -> todas las fotos/firmas offline invisibles (carrera
        // reproducida en ui-photo-reentry-race.smoke.mjs).
        if (data?.data?.id != null) {
          const occId = String(data.data.id)
          return currentReady.then(() => isLatestRequest() && Promise.all([
            this.overlayPendingOfflineAddons(occId),
            this.overlayPendingDeletedAddons(occId),
            this.overlayPendingOfflineAnswers(occId),
            this.overlayPendingOfflineSignatures(occId),
            this.overlayPendingSignatureBackup(occId),
            this.overlayPendingComments(occId),
            this.overlayPendingLocalClose(occId),
            this.overlayPendingAssignments(occId)
          ])).then(() => data, () => data)
        }
        return data
      })
      .catch(err => {
        const marked = err instanceof Error ? err : new Error('occurrence load failed')
        marked.occurrenceLoadNotified = true
        if (!isLatestRequest()) throw marked
        this.loading = false
        Notify.create({
          type: 'negative',
          message: i18n.global.t('sync.occurrenceLoadError')
        })
        console.error('[occurrences.find] Error al cargar la auditoría', err)
        throw marked
      })
  },
  reloadTasks (id) {
    const promise = OccurrencesService.reloadTasks(id)

    promise.then(data => {
      if (data.task_on_form) {
        this.tasksForm = data.task_on_form.map(task => {
          return new Task(task)
        })
      }
      if (data.task_on_occ_editing) {
        this.tasksEditing = data.task_on_occ_editing.map(task => {
          return new Task(task)
        })
      }
    })

    return promise
  },
  // El borrado no se espera a proposito: sale antes que la recarga, igual que cuando lo llamaba Header.
  clearOccurrenceTasks (occurrenceId) {
    TasksService.deleteOccTask(occurrenceId)
    return this.reloadTasks(occurrenceId)
  },
  updateSingleTask (payload) {
    const id = payload.task.id
    const type = payload.type
    const info = payload.task[type]
    const task = this.tasksEditing.find(element => element.id === id)
    const index = this.tasksEditing.indexOf(task)

    const obj = { index, type, info }
    this.tasksEditing[obj.index][obj.type] = obj.info
  },
  // ---- Optimistic store helpers (offline_tasks, ADITIVO) ----
  // Reemplazan a reloadTasks (lectura del server) cuando se trabaja sin conexión.
  // No se invocan nunca con el flag apagado.
  addOptimisticTask (rawTask) {
    const task = new Task(rawTask)
    // El client_uuid hace de id temporal: distingue task creada offline (id no
    // numérico) de una existente del server (id numérico) en editar/borrar.
    task.id = rawTask.client_uuid
    // El modelo solo mapea `task_pictures` (shape del server); las fotos del create optimista
    // llegan en `pictures` (base64) y se reflejan con id:null (aún sin id de servidor). `ev_uuid`
    // (picture_uuids, mismo orden) liga cada foto con su op para resolver su id al editar/borrar.
    if (Array.isArray(rawTask.pictures) && rawTask.pictures.length) {
      const uuids = Array.isArray(rawTask.picture_uuids) ? rawTask.picture_uuids : []
      task.picture = rawTask.pictures.filter(Boolean).map((content, index) => ({ content, id: null, ev_uuid: uuids[index] ?? null }))
    }
    // El modelo solo llena assigned_to_id desde assigned_to_data (shape del server); la task
    // optimista trae `assigned_to` numérico, que caía en el campo de NOMBRE y dejaba assigned_to_id
    // en null: la tarjeta no mostraba usuario (TKT-1975). `assigned_to_id`/`assigned_to_name`
    // explícitos mandan sobre el id crudo: la asignación inicial puede decidir "sin usuario" aunque
    // al server viaje un responsable provisional (la columna no admite null).
    task.assigned_to_id = rawTask.assigned_to_id !== undefined
      ? rawTask.assigned_to_id
      : (typeof rawTask.assigned_to === 'number' ? rawTask.assigned_to : null)
    task.assigned_to = rawTask.assigned_to_name ?? ''
    task.pending_assignment = rawTask.pending_assignment === true
    if (!Array.isArray(this.tasksEditing)) this.tasksEditing = []
    this.tasksEditing.push(task)
  },
  updateOptimisticTaskFields (id, changes) {
    if (!Array.isArray(this.tasksEditing)) return
    const task = this.tasksEditing.find(element => element.id === id)
    if (!task) return
    Object.keys(changes || {}).forEach(key => {
      task[key] = changes[key]
    })
  },
  removeOptimisticTask (id) {
    if (!Array.isArray(this.tasksEditing)) return
    const index = this.tasksEditing.findIndex(element => element.id === id)
    if (index !== -1) this.tasksEditing.splice(index, 1)
  },
  // ---- Asignación inicial de tareas de plan de acción (TKT-1975) ----
  // Solo se decide AL CREAR la tarea (automática o manual). Después, lo guardado es decisión del
  // usuario: nada de esto se vuelve a evaluar al recargar, reabrir o cambiar de rol.
  //
  // Rol: automática -> rol configurado en el formulario -> rol del auditado -> rol de la ocurrencia.
  //      manual     -> rol del auditado -> rol de la ocurrencia (la config del formulario no aplica).
  actionPlanRoleFor ({ auto }) {
    const occurrence = this.current
    const configuredRole = auto ? this.actionPlanSetting()?.role_id : null
    return configuredRole ?? occurrence?.audited_role?.id ?? occurrence?.role_id ?? null
  },
  actionPlanSetting () {
    const settings = this.current?.form?.action_plan_settings
    return Array.isArray(settings) && settings.length ? settings[0] : null
  },
  // Prioridad y fecha límite que el back pone a la tarea automática desde la config del formulario
  // (TasksController@fromForms). La tarea optimista las necesita igual: el cierre de la ocurrencia
  // guarda lo que haya en memoria y exige prioridad y fecha (isTaskIncomplete).
  actionPlanDefaults () {
    const setting = this.actionPlanSetting()
    if (!setting) return null
    return {
      priority: Number(setting.priority) || null,
      end_date: this.actionPlanEndDate(setting)
    }
  },
  // TKT-2511: la Vigencia (days) es opcional. Si viene vacía/0, el plazo se deriva de la Prioridad
  // vía action_plan_priorities de la organización (unit 'h'/'d'), igual que EditingSingleTask. Si la
  // org no tiene el ajuste cargado/configurado (time 0, o sin red / fetch async pendiente), se usa
  // el default por prioridad de los flujos manuales del front (Baja 10d, Media 5d, Alta 1d) y nunca
  // antes de mañana — así la tarea automática no nace venciendo hoy. Una Vigencia capturada (>0) se
  // respeta tal cual.
  actionPlanEndDate (setting) {
    const priority = Number(setting.priority)
    const days = Number(setting.days) || 0
    if (days > 0) {
      return this.actionPlanDeadline({ days })
    }
    const match = (useOrganizationStore().settings?.action_plan_priorities || [])
      .find(p => Number(p.value) === priority)
    const time = match ? Number(match.time) || 0 : 0
    if (time > 0) {
      return this.actionPlanDeadline(match.unit === 'h' ? { hours: time } : { days: time })
    }
    const fallbackDays = { 1: 10, 2: 5, 3: 1 }[priority] || 1
    return this.actionPlanDeadline({ days: fallbackDays })
  },
  // Nunca antes de mañana (igual que EditingSingleTask / TaskDialogForms).
  actionPlanDeadline (offset) {
    const today = date.formatDate(Date.now(), 'YYYY-MM-DD')
    const tomorrow = date.formatDate(date.addToDate(Date.now(), { days: 1 }), 'YYYY-MM-DD')
    const target = date.formatDate(date.addToDate(Date.now(), offset), 'YYYY-MM-DD')
    return target > today ? target : tomorrow
  },
  fetchAssigneeCandidates ({ roleId, locationId }) {
    const occurrenceId = this.current?.id ?? null
    if (assigneeCandidatesCache.occurrenceId !== occurrenceId) {
      assigneeCandidatesCache.occurrenceId = occurrenceId
      assigneeCandidatesCache.byKey = new Map()
    }
    const key = `${roleId}:${locationId}`
    if (!assigneeCandidatesCache.byKey.has(key)) {
      const request = withTimeout(
        UsersService.fetchByRolesAndLocations({ role_id: roleId, locations: [locationId] }),
        ASSIGNEE_CANDIDATES_TIMEOUT_MS
      ).then(users => {
        if (!Array.isArray(users)) throw new Error('users-by-role: respuesta inesperada')
        return users
      })
      assigneeCandidatesCache.byKey.set(key, request)
      request.catch(() => {
        if (assigneeCandidatesCache.byKey.get(key) === request) assigneeCandidatesCache.byKey.delete(key)
      })
    }
    return assigneeCandidatesCache.byKey.get(key)
  },
  // Usuario inicial, siempre dentro de la lista del selector (usuarios del rol en la sucursal):
  // el auditado si está en ella -> el primero de la lista -> nadie si la lista está vacía.
  // Sin red no hay lista: `pending` = no se pudo decidir; la tarjeta lo resuelve al reconectar.
  // `online: false` (flag $online del caller) evita esperar el timeout cuando ya se sabe que no hay red.
  async resolveInitialAssignee ({ roleId, locationId, online = true }) {
    if (!roleId || !locationId) return { userId: null, userName: '', users: [], pending: false }
    if (!online) return { userId: null, userName: '', users: [], pending: true }
    let users
    try {
      users = await this.fetchAssigneeCandidates({ roleId, locationId })
    } catch {
      return { userId: null, userName: '', users: [], pending: true }
    }
    const auditedId = this.current?.audited_user?.id
    const chosen = users.find(user => auditedId != null && user.id === auditedId) || users[0] || null
    return { userId: chosen?.id ?? null, userName: chosen?.name ?? '', users, pending: false }
  },
  async markTaskAssignmentPending (clientUuid) {
    try {
      await offlineTasks.markAssignmentPending(clientUuid)
    } catch (e) {
      captureOfflineError(e, { op: 'task.assignmentPending.mark' })
    }
  },
  async isTaskAssignmentPending (taskId) {
    if (!isOfflineCaptureReady()) return false
    try {
      return await offlineTasks.isAssignmentPending(taskId)
    } catch (e) {
      captureOfflineError(e, { op: 'task.assignmentPending.read' })
      return false
    }
  },
  async clearTaskAssignmentPending (taskId) {
    const task = Array.isArray(this.tasksEditing) ? this.tasksEditing.find(element => element.id === taskId) : null
    if (task) task.pending_assignment = false
    if (!isOfflineCaptureReady()) return
    try {
      await offlineTasks.clearAssignmentPending(taskId)
    } catch (e) {
      captureOfflineError(e, { op: 'task.assignmentPending.clear' })
    }
  },
  forDateAndRole (payload) {
    const promise = OccurrencesService.forDateAndRole(payload)

    promise.catch(err => console.error(err))

    return promise
  },
  update (payload) {
    return OccurrencesService.update(payload.id, payload)
      .catch(err => {
        console.error('[UPDATE ACTIONS]', err)
        throw err
      })
  },
  async resyncMissingBeforeClose ({ onResyncStart, onResyncEnd } = {}) {
    const visibleGroupIds = new Set(this.currentFormGroupById)
    const filteredOccurrence = {
      ...this.current,
      form: {
        ...this.current.form,
        items: this.current.form.items.filter(item => visibleGroupIds.has(item.group_id))
      }
    }

    let {
      missingAnswers,
      missingAddons
    } = await OccurrencesService.validateSyncStatus(filteredOccurrence, this.current.form_assignment_id)

    // Si hay respuestas faltantes en el servidor pero disponibles en el store
    // local, intentar re-sincronizarlas antes de bloquear al usuario.
    if (missingAnswers.length) {
      const answersToResync = missingAnswers
        .map(itemId => this.current.occurrence_answers.find(
          a => Number(a.item_id) === Number(itemId)
        ))
        .filter(a => a && a.value !== null && a.value !== undefined)

      if (answersToResync.length) {
        try {
          onResyncStart?.('answers')

          const payload = answersToResync.map(a => {
            const obtained = this.currentObtainedPointsByItem.find(p => Number(p.id) === Number(a.item_id))
            const possible = this.currentPossiblePointsByItem.find(p => Number(p.id) === Number(a.item_id))
            return {
              item_id: a.item_id,
              value: a.value,
              extra_properties: a.extra_properties || [],
              current_points: obtained?.obtained_points ?? 0,
              total_points: possible?.possible_points ?? 0,
              ia_image_evaluation_justification: a.ia_image_evaluation_justification ?? null
            }
          })

          const resyncPayload = {
            id: this.current.id,
            answers: payload,
            form_assignment_id: this.current.form_assignment_id,
            signatures: [],
            score: this.currentScore,
            self_evaluation_result: this.currentSelfEvaluationResult
          }
          if (this.extraPropertiesSumPayload !== null) {
            resyncPayload.extra_properties_sum = this.extraPropertiesSumPayload
          }
          const resync = await this.update(resyncPayload)

          if (!resync?.ok) {
            throw new Error(resync?.error?.message || 'Re-sync request failed')
          }

          // Re-validar tras el re-sync
          const refreshed = await OccurrencesService.validateSyncStatus(filteredOccurrence, this.current.form_assignment_id)
          missingAnswers = refreshed.missingAnswers
          missingAddons = refreshed.missingAddons
        } catch (err) {
          console.error('[resyncMissingBeforeClose] Error en re-sync:', err)
        } finally {
          onResyncEnd?.()
        }
      }
    }

    if (missingAddons.length) {
      const commentsToResync = buildCommentsToResync(missingAddons, this.current.addons, this.current.id)

      if (commentsToResync.length) {
        try {
          onResyncStart?.('comments')

          const resync = await this.update({
            id: this.current.id,
            answers: [],
            addons_values: commentsToResync,
            form_assignment_id: this.current.form_assignment_id,
            signatures: []
          })

          if (!resync?.ok) {
            throw new Error(resync?.error?.message || 'Re-sync comments request failed')
          }

          const refreshed = await OccurrencesService.validateSyncStatus(filteredOccurrence, this.current.form_assignment_id)
          missingAnswers = refreshed.missingAnswers
          missingAddons = refreshed.missingAddons
        } catch (err) {
          console.error('[resyncMissingBeforeClose] Error en re-sync de comentarios:', err)
        } finally {
          onResyncEnd?.()
        }
      }
    }

    return { missingAnswers, missingAddons }
  },
  async setNAGroup (groupId) {
    if (isOfflineCaptureReady()) {
      const occurrenceId = this.current.id.toString()
      const items = this.currentFormItems.filter(item => item.group_id === groupId)
      try {
        await offlineOccurrences.setNAGroup(occurrenceId, { groupId, itemIds: items.map(item => item.id) })
        items.forEach(item => this.setCurrentOccurrenceAnswer({ item_id: item.id, value: 'na' }))
        scheduleSyncNudge()
      } catch (err) {
        captureOfflineError(err, { op: 'occurrence.setNAGroup.v2', group_id: groupId })
        Notify.create({ type: 'negative', message: i18n.global.t('sync.saveAnswerFailed') })
      }
      return
    }

    // Se capturan los items una sola vez: el grupo puede dejar de estar visible entre el click y la
    // respuesta del server (p.ej. si cambia/borra la respuesta disparadora), y re-leer el getter
    // devolveria undefined -> crash tragado por el catch y items atorados en uploadingItems.
    const items = this.currentFormGroups[groupId] || []
    if (!items.length) return
    items.forEach(item => {
      this.uploadingItems.push(item.id)
    })

    OccurrencesService.setNAGroup(this.current.id, { group_id: groupId })
      .then(() => {
        items.forEach(item => {
          const answer = { item_id: item.id, value: 'na' }
          this.setCurrentOccurrenceAnswer(answer)
        })
      }).catch(err => {
        console.error(err)
      }).finally(() => {
        // Limpieza en finally: si el request falla, los ids igual deben salir de uploadingItems o el
        // boton de cerrar/guardar queda bloqueado hasta recargar (ver Occurrence.vue :loading).
        items.forEach(item => {
          const idx = this.uploadingItems.indexOf(item.id)
          if (idx !== -1) this.uploadingItems.splice(idx, 1)
        })
      })
  },
  resetOccurrence (occurrence_id) {
    const promise = OccurrencesService.reset(occurrence_id)

    promise.then(async () => {
      // Borrar en el servidor no bastaba: las filas locales sobrevivían y el overlay de find() las
      // devolvía a la pantalla (las respuestas-foto se superponen incluso con su op ya `done`), así
      // que reaparecía lo recién borrado. Best-effort: el reset del servidor ya ocurrió.
      try {
        await offlineOccurrences.clearLocalAnswers(occurrence_id)
      } catch (e) {
        offlineDbgWarn('reset', 'no se pudo limpiar el espejo local', e)
      }
      this.find(occurrence_id)
    })

    return promise
  },
  // Superpone sobre this.current los addons-foto capturados OFFLINE que aún NO se subieron (op de
  // outbox pending/in_flight), resolviendo su binario a base64 para que la UI los muestre. Se llama
  // tras find(). setAddons coalescea por (item_id,type): si el server ya trajo
  // ese slot, actualiza su answer; si no, lo agrega. LWW: al subirse (op -> done) dejan de calificar
  // y el server los sirve -> no se duplican. Best-effort: cualquier fallo se traga (no rompe la carga).
  async overlayPendingOfflineAddons (occurrenceId) {
    try {
      const pending = await offlineOccurrences.listPendingAddonPhotos(occurrenceId)
      // find()s solapados: si otra carga ya cambió la ocurrencia actual durante el await, NO superponer
      // (escribiríamos los addons de una occ sobre otra, mismos item_id = datos ajenos en pantalla).
      if (String(this.current?.id) !== String(occurrenceId)) return
      for (const p of pending) {
        this.setAddons({ item_id: p.item_id, type: p.type, answer: p.answer, answer_ref: p.answer_ref })
      }
      const pendingText = await offlineOccurrences.listPendingTextAddons(occurrenceId)
      if (String(this.current?.id) !== String(occurrenceId)) return
      for (const p of pendingText) {
        this.setAddons({ item_id: p.item_id, type: p.type, answer: p.answer })
      }
      if (pending.length || pendingText.length) {
        offlineDbg('photo', `overlay re-entrada: ${pending.length} foto + ${pendingText.length} texto en occ ${occurrenceId}`)
      }
    } catch (e) {
      offlineDbgWarn('photo', 'overlay de addons offline pendientes falló (no crítico)', e)
    }
  },
  // OVERLAY DE RE-ENTRADA — QUITA los addons BORRADOS offline que el server todavía devuelve (su delete no
  // ha drenado, o el server lo conservó). Simétrico a overlayPendingOfflineAddons, que RE-PINTA lo
  // agregado: find() reconstruye this.current desde el server, que aún trae el addon borrado -> reaparecía
  // y quedaba pegado en la vista sin poder abrirlo (bug visual TKT-2280). Corre en el mismo Promise.all;
  // opera sobre slots DISJUNTOS de los que re-pinta el overlay de arriba (image_ref NULL vs NOT NULL).
  // Best-effort: un fallo NO rompe la carga.
  async overlayPendingDeletedAddons (occurrenceId) {
    try {
      const deleted = await offlineOccurrences.listPendingDeletedAddons(occurrenceId)
      // find()s solapados (reintentos + navegación): si otra carga ya cambió la ocurrencia actual durante
      // el await, NO mutar. Este overlay ELIMINA slots, así que hacerlo sobre otra ocurrencia borraría
      // datos ajenos (los overlays de adición solo agregan dato viejo; este es destructivo).
      if (String(this.current?.id) !== String(occurrenceId)) return
      if (!deleted.length || !this.current.addons) return
      for (const d of deleted) {
        const list = this.current.addons[d.item_id]
        if (!Array.isArray(list)) continue
        const index = list.findIndex(a => a.type === d.type)
        if (index !== -1) list.splice(index, 1)
      }
      offlineDbg('photo', `overlay re-entrada: ${deleted.length} addon(s) borrado(s) ocultados en occ ${occurrenceId}`)
    } catch (e) {
      offlineDbgWarn('photo', 'overlay de borrados de addons offline falló (no crítico)', e)
    }
  },
  // La otra mitad del mismo invariante: las ANSWERS-foto (item-foto) capturadas offline y aún no
  // subidas tampoco vienen en el find() del server -> el ítem volvía a mostrar la cámara virgen y la
  // foto parecía perdida. Se superponen igual que los addons. Mismo LWW (solo mientras el op siga
  // pending/in_flight) y mismo best-effort: un fallo NO rompe la carga.
  async overlayPendingOfflineAnswers (occurrenceId) {
    try {
      // PASO 1 — VALORES de TODAS las respuestas pendientes (opción/puntaje), CON o SIN foto. Sin esto,
      // una respuesta de SOLO OPCIÓN (Sí/No/NA sin foto) NO se superponía y al reingresar se veía en
      // BLANCO, aunque la fila sigue durable en SQLite y sincroniza (no-clobber del down-sync). Va
      // PRIMERO: para los ítems donde la foto ES el valor de la respuesta, el overlay de fotos (PASO 2)
      // la sobrescribe con el base64.
      const pendingValues = await offlineOccurrences.listPendingAnswers(occurrenceId)
      // find()s solapados: setCurrentOccurrenceAnswer SOBRESCRIBE la answer; si otra carga ya cambió la
      // ocurrencia actual durante el await, NO superponer (pisaría respuestas de otra occ).
      if (String(this.current?.id) !== String(occurrenceId)) return
      for (const p of pendingValues) {
        void this.setCurrentOccurrenceAnswer(mergePendingAnswer(this.current?.occurrence_answers || [], p))
      }

      // PASO 2 — FOTOS de las respuestas-foto pendientes (base64 resuelto). Comportamiento previo.
      const pending = await offlineOccurrences.listPendingAnswerPhotos(occurrenceId)
      if (String(this.current?.id) !== String(occurrenceId)) return
      for (const p of pending) {
        void this.setCurrentOccurrenceAnswer(mergePendingAnswer(this.current?.occurrence_answers || [], p))
      }
      if (pendingValues.length) offlineDbg('offline', `overlay re-entrada: ${pendingValues.length} respuesta(s) offline superpuestas (${pending.length} con foto) en occ ${occurrenceId}`)

      // TKT-2356: lo que quedó en DEAD-LETTER ya se pintó (no desaparece del formulario), pero el
      // usuario debe saber que NO llegó al server: sin este aviso la pérdida solo era visible en el
      // drawer de sync, que nadie abre a media captura. Aviso puntual (una vez por carga).
      const deadCount = pendingValues.filter(p => p.sync_status === 'dead').length
      if (deadCount > 0) {
        Notify.create({
          type: 'warning',
          message: i18n.global.t('sync.answersNotSent', { n: deadCount }),
          timeout: 8000
        })
      }
    } catch (e) {
      offlineDbgWarn('offline', 'overlay de respuestas offline pendientes falló (no crítico)', e)
    }
  },
  // OVERLAY DE RE-ENTRADA (ISS-994): FIRMAS capturadas OFFLINE que aún no se subieron. Última mitad
  // del invariante que ya cumplían addons/answers: sin este overlay, una firma capturada offline
  // sobrevivía durable en SQLite pero desaparecía de la pantalla en cada reingreso hasta que el
  // outbox drenaba (nunca se agregó cuando offline-v2 shippeó, PR #145). A diferencia de
  // addons/answers (coalesce por item_id/slot -> setAddons/setCurrentOccurrenceAnswer pisan la MISMA
  // entrada), las firmas son EVENTS append-only (this.current.signatures es un array que
  // OccurrenceSignaturesSection.vue empuja con signatureList.push en signed()): este overlay corre en CADA find(), así
  // que hay que evitar duplicar la MISMA firma pendiente entre dos reingresos. `client_uuid` es la
  // clave estable; se estampa como `_pendingClientUuid` (efímero, NUNCA se manda al server, mismo
  // espíritu que `_pendingLocalClose` de abajo) y, antes de empujar la versión fresca, se filtra
  // cualquier entrada previa con el MISMO `_pendingClientUuid` para no duplicarla. listPendingSignatures
  // devuelve SOLO ops pending/in_flight (NO 'done', a diferencia de los overlays de foto): una firma ya
  // subida la sirve el server en find(), y re-empujarla aquí la DUPLICABA (append-only). Best-effort: un
  // fallo NO rompe la carga.
  async overlayPendingOfflineSignatures (occurrenceId) {
    try {
      const pending = await offlineOccurrences.listPendingSignatures(occurrenceId)
      // find()s solapados: si otra carga cambió la ocurrencia durante el await, NO empujar firmas de una
      // occ sobre otra (bleed cruzado, y las firmas son append-only visibles).
      if (!pending.length || String(this.current?.id) !== String(occurrenceId)) return
      if (!Array.isArray(this.current.signatures)) this.current.signatures = []
      for (const p of pending) {
        // Fallback SOLO para filas legacy pre-v10 (sin las columnas nuevas, ver migrations.js): se
        // reconstruye is_auditor/is_audited desde signature_kind. Las filas escritas por el
        // addSignature actual ya traen los booleans reales -> se prefieren cuando no son null.
        const isAuditor = p.is_auditor != null ? !!p.is_auditor : p.signature_kind === 'auditor'
        const isAudited = p.is_audited != null ? !!p.is_audited : p.signature_kind === 'audited'
        this.current.signatures = this.current.signatures.filter(s => s._pendingClientUuid !== p.client_uuid)
        this.current.signatures.push({
          signature_path: p.signature_path,
          signed_by: p.signed_by,
          signed_name: p.signed_name,
          is_auditor: isAuditor,
          is_audited: isAudited,
          audited_rol_name: p.audited_rol_name,
          audited_name: p.audited_name,
          _pendingClientUuid: p.client_uuid
        })
      }
      offlineDbg('offline', `overlay re-entrada: ${pending.length} firma(s) offline superpuestas en occ ${occurrenceId}`)
    } catch (e) {
      offlineDbgWarn('offline', 'overlay de firmas offline pendientes falló (no crítico)', e)
    }
  },
  // TKT-1975: una tarea creada sin red vuelve del server con el responsable PROVISIONAL (la columna no
  // admite null), no con uno elegido de la lista del rol. Al reabrir se deja sin usuario en memoria:
  // el cierre la bloquea (isTaskIncomplete) hasta que la tarjeta aplique la asignación inicial. Sin
  // esto, una tarjeta que aún no se monta (q-intersection) dejaba pasar el provisional al cierre.
  async overlayPendingAssignments (occurrenceId) {
    try {
      if (!Array.isArray(this.tasksEditing) || !isOfflineCaptureReady()) return
      for (const task of this.tasksEditing) {
        const assignedBefore = task.assigned_to_id
        if (!(await offlineTasks.isAssignmentPending(task.id))) continue
        if (String(this.current?.id) !== String(occurrenceId)) return
        // Si la tarjeta ya resolvió la asignación durante el await, se respeta lo que eligió.
        if (task.assigned_to_id !== assignedBefore) continue
        task.assigned_to_id = null
        task.assigned_to = ''
        task.pending_assignment = true
      }
    } catch (e) {
      offlineDbgWarn('tasks', 'overlay de asignaciones pendientes falló (no crítico)', e)
    }
  },
  // OVERLAY DE RE-ENTRADA — CIERRE LOCAL pendiente (fix bug de reingreso editable). find() reconstruye
  // this.current desde el SERVER; si la occurrence se FINALIZÓ offline-first pero el occurrence.close AÚN
  // no drenó, el server la devuelve is_closed=false -> al reabrirla se veía EDITABLE con los datos viejos
  // (como si no se hubiera finalizado; el usuario podía re-editarla y re-finalizar). Se fuerza
  // is_closed=true mientras exista el close local pendiente/in_flight -> FINALIZAR oculto (Occurrence.vue:81),
  // OccurrenceFillForm locked (isFormLocked), validateOccurrence pasa. `_pendingLocalClose` (efímero, no se
  // persiste) guarda el botón "Autorizar" para que un autorizador no autorice una occ que el server aún
  // ve ABIERTA. Tras drenar: hasPendingClose=false y el server ya la devuelve cerrada / una occ nueva ->
  // comportamiento normal restaurado. Best-effort/simétrico con los otros overlays: un fallo NO rompe la
  // carga.
  async overlayPendingLocalClose (occurrenceId) {
    try {
      if (await offlineOccurrences.hasPendingClose(occurrenceId)) {
        // is_closed=true hace que currentScore (getters.js) devuelva this.current.score tal cual
        // — pero find() lo reconstruyó desde el server, que aún ve la occurrence ABIERTA (el close
        // sigue sin drenar) -> score quedaba undefined/vacío. Se restaura desde la fila local
        // (poblada por markClosing al encolar el cierre).
        await markCurrentClosedPending(this, occurrenceId, () => offlineOccurrences.getPendingCloseScore(occurrenceId))
        offlineDbg('offline', `overlay re-entrada: occ ${occurrenceId} con cierre local pendiente -> read-only`)
        return
      }
    } catch (e) {
      offlineDbgWarn('offline', 'overlay de cierre local pendiente falló (no crítico)', e)
    }
    try {
      const deferredClose = await findDeferredClose(occurrenceId)
      if (deferredClose) {
        await markCurrentClosedPending(this, occurrenceId, () => deferredClose.payload?.score)
        offlineDbg('offline', `overlay re-entrada: occ ${occurrenceId} con cierre diferido -> read-only`)
      }
    } catch (e) {
      offlineDbgWarn('offline', 'overlay de cierre diferido falló (no crítico)', e)
    }
  },
  setAddons (addon) {
    if (!addon || !addon.item_id || !addon.type) {
      console.warn('[setAddon ignorado por datos incompletos]', addon)
      return
    }

    if (this.current.addons[addon.item_id] === undefined) this.current.addons[addon.item_id] = []

    const addonIndex = this.current.addons[addon.item_id].findIndex(_addon => _addon.type === addon.type)
    if (addonIndex > -1) {
      this.current.addons[addon.item_id][addonIndex].answer = addon.answer
      if ('thumbnail' in addon) this.current.addons[addon.item_id][addonIndex].thumbnail = addon.thumbnail
    } else this.current.addons[addon.item_id].push(addon)
  },
  isPrinting (printing) {
    this.printing = printing
  },
  addDocument (itemId) {
    seedEmptyTextAddon(this, itemId, 'document')
  },
  addComment (itemId) {
    seedEmptyTextAddon(this, itemId, 'comment')
  },
  // offline (SQLite): encola un addon (comentario/documento/foto) por la fachada de dominio
  // cuando la base está abierta. Escribe data+outbox (coalesce por client_uuid) y nudgea
  // el drain. Es offline-FIRST (encola siempre, online o no). El `setAddons` en memoria ya corrió
  // en el llamador para que la UI lo muestre; esto reemplaza SOLO la persistencia/red del addon.
  // Foto en base64 (web/Android) -> param `photoBase64` (PhotoStore -> photo_meta + image_ref +
  // binario fuera de SQLite). Capacitor file:// no se enruta aquí (el binario nativo lo resuelve el
  // camino legacy). Base cerrada ⇒ no-op (el camino legacy del llamador queda intacto).
  // `image_ref`/`local_path` explícitos (incluido null) pasan directo al dominio: null limpia la ref
  // sticky al BORRAR un addon de foto (sin esto el drain re-subiría la foto borrada). undefined = sticky.
  // DEVUELVE la promesa de persistencia (resuelve cuando la fila+binario son DURABLES): los flujos
  // de foto la awaitean para cerrar la carrera de "capturar y cerrar el form/app al instante" en
  // device (iOS suspende el JS al cerrar: una cadena fire-and-forget moría sin fila y la foto se
  // perdía en silencio — visto en campo con la 3ª foto de un ítem). Callers de texto pueden seguir
  // sin awaitear (el .catch interno mantiene el reporte anti-pérdida y evita unhandled rejection).
  enqueueAddonV2 ({
    item_id: itemId, type, answer, answer_ref: answerRef, photoBase64, fileBase64, filename,
    image_ref: imageRef, local_path: localPath
  } = {}) {
    // ISS-1009: tambien corta con la base CERRADA. Los callers ya vienen gateados; esta es la
    // segunda linea de defensa para que nadie encole contra un backend nulo.
    if (!isOfflineStorageReady()) return Promise.resolve()
    try {
      const occurrenceId = String(this.current.id)
      // client_uuid DETERMINISTA por (occurrence, item, type): así el placeholder vacío que crean
      // addComment/addDocument y el texto/valor que llega DESPUÉS (updateAddon) coalescen sobre la
      // MISMA fila de addon en SQLite (PK = client_uuid; outbox coalesce por client_uuid) en vez de
      // crear filas duplicadas. El addon de texto NUNCA debe quedar como dos filas (placeholder +
      // texto): debe terminar como UNA fila con el texto (FIX 6).
      const clientUuid = `addon:${occurrenceId}:${itemId}:${type}`
      // addAddon es async (escribe SQLite + PhotoStore/NativeFsBackend). En device el write del
      // filesystem SÍ puede rechazar; el .catch mantiene el rechazo manejado (reporte durable) aunque
      // el caller no awaitee; el caller que SÍ awaitea recibe el rechazo por su propia rama.
      // SERIALIZADO por client_uuid: el placeholder vacío (addComment/addDocument), los debounces
      // intermedios y el valor final de un mismo addon commitean en orden de llegada, así un parcial
      // tardío nunca pisa al final (TKT-2280). Los ids ya están capturados; addAddon corre dentro de
      // la cadena, la red/FS del write anterior incluida.
      const persist = addonWriteSerializer(clientUuid, () =>
        offlineOccurrences.addAddon({
          occurrence_id: occurrenceId,
          item_id: itemId,
          type,
          answer,
          answer_ref: answerRef,
          client_uuid: clientUuid,
          photoBase64,
          fileBase64,
          filename,
          image_ref: imageRef,
          local_path: localPath
        })
      )
      persist.catch((err) => {
        offlineDbgError('photo', 'addAddon falló (addon/foto/archivo NO persistido durablemente)', err)
        // Anti pérdida-silenciosa: si era un addon con BINARIO (foto o archivo), deja rastro DURABLE para
        // que TheSyncDrawer/Banner avise (antes solo iba al ring buffer en memoria).
        if (photoBase64 || fileBase64) reportPhotoCaptureFailed({ occurrenceId, itemId })
      })
      persist.then(() => { scheduleSyncNudge() }, () => {})
      return persist
    } catch (e) {
      offlineDbgError('photo', 'error encolando addon offline', e)
      // Rechazada PRE-manejada: el caller que awaitea recibe el error; el que no, no genera
      // unhandled rejection (el .catch vacío ya lo marca como manejado).
      const rejected = Promise.reject(e)
      rejected.catch(() => {})
      return rejected
    }
  },
  async addImage (addon) {
    if (addon.value && !addon.answer) {
      addon.answer = addon.value
    }

    const pictureAddons = PICTURE_ADDON_TYPES

    let saved = false
    const occurrenceId = this.current.id

    async function buildBackendAddonPayload (uiAddon) {
      // Blob ya disponible directamente (componentes B.5+)
      if (uiAddon.blob instanceof Blob) {
        const answer_ref = uiAddon.answer_ref || `ans-${Date.now()}-${uiAddon.item_id}`
        return {
          item_id: uiAddon.item_id,
          type: uiAddon.type,
          answer_ref,
          file: uiAddon.blob
        }
      }

      let file = null

     if (Capacitor.isNativePlatform() && uiAddon.localPath) {
       const blob = await readImageAsBlob(uiAddon.localPath)

        if (!blob) {
          return null
        }

        const answer_ref = `ans-${Date.now()}-${uiAddon.item_id}`

        return {
          item_id: uiAddon.item_id,
          type: uiAddon.type,
          answer_ref,
          file: blob
        }
      }

      if (!file && uiAddon.answer && uiAddon.answer.startsWith('data:image')) {
        try {
          const res = await fetch(uiAddon.answer)
          const blob = await res.blob()
          file = new File(
            [blob],
            `img-${Date.now()}-${uiAddon.item_id}.jpg`,
            { type: blob.type }
          )
        } catch (e) {
          console.error(e)
        }
      }

      if (!file) return null

      return {
        item_id: uiAddon.item_id,
        type: uiAddon.type,
        file
      }
    }

    async function processSlot (self, baseAddon, uploadKey) {
      const uiAddon = {
        ...baseAddon,
        occurrence_id: occurrenceId,
        form_assignment_location_occurrence_id: occurrenceId
      }

      if (uiAddon.thumbnail && uiAddon.answer && uiAddon.answer.length > uiAddon.thumbnail.length) {
        uiAddon.answer = uiAddon.thumbnail
      }

      self.setAddons(uiAddon)

      // offline (SQLite): el addon-foto se persiste durable
      // por la fachada (data+outbox + PhotoStore para el binario) en vez del idb legacy +
      // updateFormData online. `uiAddon.answer` es base64 (web/Android: dataURL o thumbnail);
      // se enruta por `photoBase64`. Capacitor file://|content:// (uiAddon.localPath): se LEE el
      // binario nativo a base64 y también se enruta por v2 (PhotoStore -> NativeFsBackend, ruta
      // RELATIVA durable ante reinstalación) en vez de caer al legacy v1 (nativeURL absoluto = bug
      // de reinstalación). Solo se cae al legacy si el binario nativo es ILEGIBLE. Offline-FIRST +
      // nudge. Base CERRADA (ISS-1009) ⇒ comportamiento legacy intacto.
      // [device-validation: addon-foto con cámara real]
      if (isOfflineStorageReady()) {
        let b64 =
          typeof uiAddon.answer === 'string' && uiAddon.answer.startsWith('data:image')
            ? uiAddon.answer
            : null
        if (!b64 && uiAddon.localPath && Capacitor.isNativePlatform()) {
          try {
            const read = await readImageFromFileSystem(uiAddon.localPath)
            if (typeof read === 'string' && read.startsWith('data:image')) {
              b64 = read
              offlineDbg('photo', `addon item ${uiAddon.item_id}: foto nativa file://|content:// -> base64 OK -> offline`)
            } else {
              offlineDbgWarn('photo', `addon item ${uiAddon.item_id}: readImageFromFileSystem no devolvió data:image`)
            }
          } catch (e) { offlineDbgWarn('photo', `addon item ${uiAddon.item_id}: error leyendo foto nativa`, e) }
        }
        // Blob-first (web): la ruta "PhotoEditor sin cambios" deja `uiAddon.answer` como un blob:
        // URL (efímero) y adjunta el Blob real aparte en `uiAddon.blob`. Sin esto, `b64` quedaba
        // null y el addon-foto SIEMPRE caía al legacy — y si la sesión no sobrevive hasta que el
        // legacy suba el blob: (recarga/cierre de app), el addon se pierde en silencio.
        if (!b64 && uiAddon.blob instanceof Blob && uiAddon.blob.size > 0) {
          try {
            const read = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsDataURL(uiAddon.blob)
            })
            if (typeof read === 'string' && read.startsWith('data:image')) {
              b64 = read
              offlineDbg('photo', `addon item ${uiAddon.item_id}: Blob web -> base64 OK -> offline`)
            } else {
              offlineDbgWarn('photo', `addon item ${uiAddon.item_id}: FileReader no devolvió data:image`)
            }
          } catch (e) { offlineDbgWarn('photo', `addon item ${uiAddon.item_id}: error convirtiendo Blob a base64`, e) }
        }
        if (b64) {
          // AWAIT + spinner (fix multi-foto iOS): la persistencia (SQLite + PhotoStore) debe quedar
          // DURABLE antes de soltar el flujo. Fire-and-forget dejaba una ventana en la que cerrar el
          // form/app suspendía el JS y la foto (la última capturada) moría sin fila -> el drain subía
          // N-1 fotos. El spinner (uploadingPhotos) además le dice al usuario que espere. Si la
          // persistencia v2 FALLA, se cae al camino legacy de abajo (la foto no se pierde).
          self.uploadingPhotos.push(uploadKey)
          try {
            await self.enqueueAddonV2({
              item_id: uiAddon.item_id,
              type: uiAddon.type,
              answer_ref: uiAddon.answer_ref,
              photoBase64: b64
            })
            return
          } catch (e) {
            offlineDbgWarn('photo', `addon item ${uiAddon.item_id}: persistencia v2 falló, cayendo a legacy`, e)
          } finally {
            self.uploadingPhotos.splice(self.uploadingPhotos.indexOf(uploadKey), 1)
          }
        }
        // Sin base64 utilizable (binario nativo ilegible) o persistencia v2 fallida: camino legacy.
      }

      try {
        await saveAnyOfflineAddon({ ...uiAddon })
      } catch (e) {
        console.error(e)
      }

      if (!navigator.onLine) return

      self.uploadingPhotos.push(uploadKey)

      try {
        const backendAddon = await buildBackendAddonPayload(uiAddon)

        if (!backendAddon || !backendAddon.file) {
          if (uiAddon.thumbnail) {
            uiAddon.answer = uiAddon.thumbnail
          } else if (!uiAddon.answer && uiAddon.localPath && Capacitor.isNativePlatform()) {
            try {
              const blob = await readImageAsBlob(uiAddon.localPath)
              if (blob) uiAddon.answer = URL.createObjectURL(blob)
            } catch (e) {
              console.error(e)
            }
          }

          self.setAddons(uiAddon)
          return
        }

        const payload = {
          addons_values: [backendAddon],
          signatures: []
        }

        const formData = buildOccurrenceFormData(payload)

        const res = await withTimeout(
          OccurrencesService.updateFormData(
            self.current.id,
            self.current.form_assignment_id,
            formData
          ),
          7000
        )

        const list = res?.data?.addons?.[uiAddon.item_id] || []
        const picture = list.find(a => a.type === uiAddon.type)

        if (picture && picture.answer) {
          // Revocar blob URLs locales antes de reemplazar con URL S3
          if (typeof uiAddon.answer === 'string' && uiAddon.answer.startsWith('blob:')) {
            URL.revokeObjectURL(uiAddon.answer)
          }
          if (
            typeof uiAddon.thumbnail === 'string' &&
            uiAddon.thumbnail.startsWith('blob:') &&
            uiAddon.thumbnail !== uiAddon.answer
          ) {
            URL.revokeObjectURL(uiAddon.thumbnail)
          }

          const syncedAddon = {
            ...uiAddon,
            answer: picture.answer,
            thumbnail: null  // limpiar blob URL revocado; LightboxPicture usará answer (S3) como fallback
          }

          self.setAddons(syncedAddon)

          try {
            await removeSyncedLegacyAddonRow(syncedAddon)
          } catch (e) {
            console.error(e)
          }
        }
      } finally {
        self.uploadingPhotos.splice(
          self.uploadingPhotos.indexOf(uploadKey),
          1
        )
      }
    }

    // Fix multi-foto (re-entrada device): al reabrir la occurrence, `find()` reconstruye la memoria
    // desde el SERVER y NO trae las capturas offline pendientes -> el slot-finder (que solo miraba la
    // memoria) reusaba un slot ya ocupado en SQLite y el client_uuid determinista COALESCEABA sobre
    // esa fila (la foto pendiente se PISABA = pérdida). Slots ocupados = memoria ∪ SQLite. Una fila
    // de SQLite sin binario y con answer vacío (delete) NO ocupa (reusable, igual que en memoria).
    let offlineOccupied = new Set()
    try {
      const rows = await offlineOccurrences.listAddons(String(occurrenceId), addon.item_id)
      offlineOccupied = new Set(
        rows.filter(r => r.image_ref || (r.answer != null && r.answer !== '')).map(r => r.type)
      )
    } catch (e) {
      offlineDbgWarn('photo', 'no se pudieron leer los addons offline para asignar slot', e)
    }

    for (let index = 0; index < pictureAddons.length && !saved; index++) {
      const addonType = pictureAddons[index]
      const currentList = this.current.addons[addon.item_id] || []
      const includes = currentList.find(a => a.type === addonType)
      const uploadKey = `${addon.item_id}_${index + 1}`

      if (offlineOccupied.has(addonType)) continue // ocupado en SQLite (captura offline pendiente)

      if (!includes) {
        await processSlot(this, { ...addon, type: addonType }, uploadKey)
        saved = true
      } else if (includes.answer === '') {
        await processSlot(this, { ...addon, type: includes.type }, uploadKey)
        saved = true
      }
    }
  },
  async addVideo (payload) {
    const { file, itemId } = payload

    const validationErrorKey = validateVideoFile(file)
    if (validationErrorKey) {
      Notify.create({ type: 'negative', message: i18n.global.t(validationErrorKey) })
      return
    }

    const timestamp = Date.now()
    const ref = `vid-${timestamp}-${itemId}`
    const occurrenceId = this.current.id
    const uploadKey = `${itemId}_video`
    const blobUrl = URL.createObjectURL(file)

    const uiAddon = {
      item_id: itemId,
      type: 'video',
      occurrence_id: occurrenceId,
      form_assignment_location_occurrence_id: occurrenceId,
      answer_ref: ref,
      answer: blobUrl
    }

    // ANTI PÉRDIDA-SILENCIOSA: persistir el BINARIO primero. Si el write a IndexedDB falla (cuota llena,
    // blob de hasta 45MB en webview), NO mostramos el video optimista NI escribimos la metadata —si lo
    // hiciéramos, el drain mandaría un addon de video SIN su file y el usuario creería que se guardó
    // (pérdida silenciosa). Avisamos para re-capturar.
    try {
      await saveLegacyVideoBlob(ref, file)
    } catch (e) {
      console.error('[addVideo] error saving blob to IndexedDB', e)
      URL.revokeObjectURL(blobUrl)
      Notify.create({ type: 'negative', message: i18n.global.t('sync.videoSaveFailed') })
      return
    }
    // Binario persistido -> ahora sí mostramos el video y escribimos la metadata offline.
    this.setAddons(uiAddon)

    const offlineAddon = {
      occurrence_id: occurrenceId,
      form_assignment_location_occurrence_id: occurrenceId,
      item_id: itemId,
      type: 'video',
      answer_ref: ref
    }

    try {
      await upsertLegacyVideoRow({ occurrenceId, itemId, row: offlineAddon })
    } catch (e) {
      console.error('[addVideo] error saving offline metadata', e)
    }

    if (!navigator.onLine) return

    this.uploadingPhotos.push(uploadKey)

    try {
      const formData = new FormData()
      formData.append('addons_values[0][item_id]', itemId)
      formData.append('addons_values[0][type]', 'video')
      formData.append(
        'addons_values[0][file]',
        file,
        file.name || `video-${timestamp}-${itemId}.mp4`
      )
      formData.append('app_version', APP_VERSION)

      const res = await OccurrencesService.updateFormData(
        this.current.id,
        this.current.form_assignment_id,
        formData
      )

      if (!res?.ok) {
        notifyVideoUploadFailure(res?.error)
        return
      }

      // updateFormData wraps as { ok, data: axiosBody } and the backend body is
      // { success, data: { addons, ... } } - hence the double `.data` here.
      const serverAddons = extractServerAddons(res.data)
      const remoteUrl = findConfirmedRemoteUrl(serverAddons, offlineAddon)

      if (!remoteUrl) {
        notifyVideoUploadFailure(null)
        return
      }

      this.setAddons({
        ...uiAddon,
        answer: remoteUrl
      })
      URL.revokeObjectURL(blobUrl)

      try {
        await purgeLegacyVideo({ occurrenceId, itemId, ref })
      } catch (e) {
        console.error('[addVideo] error cleaning offline state', e)
      }
    } catch (e) {
      console.error('[addVideo] upload failed', e)
      notifyVideoUploadFailure(e)
    } finally {
      const idx = this.uploadingPhotos.indexOf(uploadKey)
      if (idx > -1) this.uploadingPhotos.splice(idx, 1)
    }
  },
  /**
   * Espera —ACOTADO— a que el cierre encolado de una occurrence llegue al server (TKT-2182).
   *
   * Con el cierre offline-first el `save-and-close` NO sale en la llamada del botón: se encola en el
   * outbox y drena en background. Cualquier acción que le pida al BACKEND generar el documento de la
   * auditoría (correo, PDF) es en cambio una llamada online DIRECTA, así que puede ganarle la carrera
   * al cierre y hacer que el backend renderice contra la occurrence todavía ABIERTA.
   *
   * `hasPendingClose` es el predicado exacto de esa ventana (fila outbox op='occurrence.close' en
   * pending/in_flight para ese occId). El handler del close ya espera a que drenen las respuestas y
   * fotos de la occurrence antes de POSTear, así que "sin cierre pendiente" implica también
   * "respuestas ya en el server".
   *
   * Devuelve true en cuanto no queda cierre pendiente — incluido el caso normal en que nunca lo hubo
   * (capa offline apagada, auditoría vieja, cierre legacy online ya confirmado): ahí sale a la
   * primera y NO añade latencia. Devuelve false si se agota el presupuesto: el caller decide, pero
   * ninguno debe mandarle al cliente un documento de una auditoría que el server aún ve abierta.
   *
   * Empuja el drain en cada vuelta (`syncNow({ force: true })`) para no depender del tick periódico
   * ni de `navigator.onLine`. Best-effort: si la capa offline truena, no bloquea la acción.
   *
   * `timeoutMs` acota cuándo se deja de ARRANCAR otro ciclo de sync, no el reloj de pared: un ciclo ya
   * en curso se espera completo. Es deliberado — verificado en el APK contra prod con la subida
   * estrangulada, el cierre tardó ~61s y el correo salió bien en vez de rebotarle al usuario a los 45s
   * un drain que estaba a punto de terminar. El tope real es `timeoutMs` + un ciclo de sync, que
   * termina siempre porque el transporte trae sus propios timeouts HTTP.
   *
   * @param {number|string} occurrenceId
   * @param {{ timeoutMs?: number, pollMs?: number }} opts
   * @returns {Promise<{drained: boolean, hadPending: boolean}>} `hadPending` distingue "nunca hubo
   *   cierre encolado" de "lo hubo y ya llegó": el segundo caso implica que la auditoría SÍ quedó
   *   finalizada, aunque `current.is_closed` en memoria siga desactualizado.
   */
  async waitForPendingCloseDrained (occurrenceId, { timeoutMs = 45000, pollMs = 1500 } = {}) {
    if (occurrenceId == null) return { drained: true, hadPending: false }

    // "Sin confirmar" = el server puede NO tener el cierre aplicado. Cubre las TRES formas en que eso
    // pasa: la fila de outbox pendiente/en vuelo, la que quedó en dead-letter, y el cierre DIFERIDO
    // (SQLite caída + sin red), que no deja fila de outbox y vive en OfflineStorage.
    const readCloseState = async (source, read) => {
      try {
        return await read(String(occurrenceId))
      } catch (err) {
        // Sin lectura fiable no se puede AFIRMAR que el cierre esté sin confirmar: no se bloquea la
        // acción del usuario (mismo criterio best-effort del resto de la capa offline).
        offlineDbgError('occ', `waitForPendingCloseDrained: no se pudo leer ${source}: ${err?.message}`)
        return false
      }
    }
    const unconfirmed = async () =>
      (await readCloseState('el cierre del outbox', id => offlineOccurrences.hasUnconfirmedClose(id))) ||
      (await readCloseState('el cierre diferido', hasDeferredClose))

    if (!(await unconfirmed())) return { drained: true, hadPending: false }

    // Sí hay que esperar: el botón se queda cargando varios segundos y sin esto el usuario no
    // entiende por qué. Se avisa UNA vez, al entrar a la espera (no en el camino normal).
    Notify.create({
      type: 'ongoing',
      icon: 'sync',
      message: i18n.global.t('sync.occurrenceSyncingBeforeDocument'),
      timeout: 4000
    })

    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      // Los dos caminos de entrega: el outbox (drain normal) y el replay del cierre diferido, que el
      // drain NO toca. Ambos son idempotentes y no lanzan al caller.
      await offlineSyncNow({ force: true }).catch(() => {})
      await replayPendingCloses().catch(() => {})
      if (!(await unconfirmed())) return { drained: true, hadPending: true }
      // Un cierre en dead-letter no se recupera solo (el drain ya no lo toma): seguir esperando solo
      // le haría perder el presupuesto al usuario. Se corta y se le pide reintentar.
      try {
        if (await offlineOccurrences.hasDeadClose(String(occurrenceId))) return { drained: false, hadPending: true }
      } catch { /* best-effort: si no se puede leer, se sigue esperando */ }
      await new Promise(resolve => setTimeout(resolve, pollMs))
    }

    return { drained: !(await unconfirmed()), hadPending: true }
  },
  async waitForPendingAnswersDrained (occurrenceId, { timeoutMs = 45000, pollMs = 1500 } = {}) {
    if (occurrenceId == null) return { drained: true }

    const unsynced = async () => {
      try {
        return await offlineOccurrences.hasUnsyncedAnswers(String(occurrenceId))
      } catch (err) {
        offlineDbgError('occ', `waitForPendingAnswersDrained: no se pudo leer el estado de las respuestas: ${err?.message}`)
        return false
      }
    }

    if (!(await unsynced())) return { drained: true }

    Notify.create({
      type: 'ongoing',
      icon: 'sync',
      message: i18n.global.t('sync.occurrenceSyncingBeforeDocument'),
      timeout: 4000
    })

    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      await offlineSyncNow({ force: true }).catch(() => {})
      if (!(await unsynced())) return { drained: true }
      let dead = false
      try { dead = await offlineOccurrences.hasDeadAnswers(String(occurrenceId)) } catch { dead = false }
      if (dead) return { drained: false }
      await new Promise(resolve => setTimeout(resolve, pollMs))
    }

    return { drained: !(await unsynced()) }
  },
  /**
   * Aviso de "esta auditoría no está finalizada" antes de mandarle el documento a alguien (TKT-2182).
   *
   * Enviar un formulario en curso sigue siendo un flujo válido, así que no se bloquea: se pide
   * confirmación diciendo con todas sus letras cómo va a salir el PDF, para que sea una decisión
   * consciente y no un accidente.
   *
   * @returns {Promise<boolean>} true = el usuario confirmó que quiere enviarlo de todas formas.
   */
  confirmSendUnfinished () {
    return new Promise(resolve => {
      Dialog.create({
        title: i18n.global.t('sync.occurrenceNotClosedTitle'),
        message: i18n.global.t('sync.occurrenceNotClosedMessage'),
        cancel: { label: i18n.global.t('sync.occurrenceNotClosedCancel'), flat: true },
        ok: { label: i18n.global.t('sync.occurrenceNotClosedOk'), color: 'binnacle-red' },
        persistent: true
      })
        .onOk(() => resolve(true))
        .onCancel(() => resolve(false))
        .onDismiss(() => resolve(false))
    })
  },
  /**
   * Marca en memoria que el usuario ACABA de finalizar esta auditoría.
   *
   * `find()` reconstruye `current` desde el server, así que tras FINALIZAR `is_closed` se queda en
   * false hasta la próxima carga — y el aviso de "sin finalizar" saltaría en el camino normal, que es
   * justo cuando NO debe salir (verificado: sin esto, el diálogo aparecía al finalizar y mandar el
   * correo, que es el flujo de todos los días).
   */
  markOccurrenceClosedLocally (occurrenceId) {
    if (this.current && String(this.current.id) === String(occurrenceId)) {
      this.current.is_closed = true
    }
  },
  /**
   * Envía por correo el PDF de una auditoría.
   *
   * TKT-2182: el correo se generaba contra la occurrence todavía ABIERTA cuando el usuario lo mandaba
   * justo después de FINALIZAR (el diálogo "¿Desea enviarlo por correo?" aparece de inmediato, pero el
   * cierre sigue drenando en el outbox). Al cliente le llegaba "Calificación 0/%" con la leyenda
   * "Este formulario aún no ha sido finalizado" — y, si las respuestas tampoco habían drenado, un PDF
   * sin ítems. Por eso el POST espera al cierre; si no drena a tiempo NO se envía nada: es preferible
   * pedir reintento que mandarle al cliente un PDF que dice lo contrario de lo que hizo el auditor.
   */
  async sendOccurrenceByEmail (payload) {
    const { drained, hadPending } = await this.waitForPendingCloseDrained(payload.occurrenceId)

    if (!drained) {
      const err = new Error(`sendOccurrenceByEmail: occurrence ${payload.occurrenceId} aún sin cerrar en el server`)
      err.occurrencePendingSync = true
      throw err
    }

    const { drained: answersDrained } = await this.waitForPendingAnswersDrained(payload.occurrenceId)

    if (!answersDrained) {
      const err = new Error(`sendOccurrenceByEmail: occurrence ${payload.occurrenceId} tiene respuestas sin sincronizar en el server`)
      err.occurrencePendingSync = true
      throw err
    }

    const avisar = shouldConfirmUnfinished({ hadPending, current: this.current, occurrenceId: payload.occurrenceId })
    if (avisar && !(await this.confirmSendUnfinished())) {
      const cancelled = new Error('sendOccurrenceByEmail: cancelado por el usuario (auditoría sin finalizar)')
      cancelled.occurrenceSendCancelled = true
      throw cancelled
    }

    const promise = OccurrencesService.sendOccurrenceByEmail(payload)
    promise.catch(err => console.error(err))
    return promise
  },
  async saveOfflineAnswers () {
    await sendPendingSignatures(this.current)
    await replayPendingComments().catch(err => console.error(err))
    try {
      return await drainLegacyAnswers({
        getOccurrenceId: () => String(this.current.id),
        sendBatch: formData => OccurrencesService.saveOfflineAnswers(formData),
        onBadNetwork: () => Notify.create({
          type: 'warning',
          message: 'La señal es muy baja, las respuestas se guardaron offline.',
          caption: 'Puedes intentar de nuevo al abrir el formulario con mejor señal.'
        }),
        onSyncStart: () => Loading.show({
          message: 'Sincronizando respuestas...',
          spinnerSize: 60,
          spinner: QSpinnerTail
        }),
        onPartial: () => Notify.create({
          type: 'warning',
          message: 'Algunas respuestas quedaron pendientes de sincronizar.',
          caption: 'Se reintentarán al reabrir la auditoría con mejor señal.'
        }),
        onSyncEnd: () => Loading.hide()
      })
    } catch (err) {
      Loading.hide()
      throw err
    }
  },

  // Comentarios adicionales de la ocurrencia (F-97). Mismo criterio que las respuestas y las firmas: primero
  // local y después al servidor. Con la base abierta -> fila occurrences.comments + op
  // occurrence.saveComments (el worker hace el PUT). Con la base cerrada (o si esa escritura falla) -> PUT en
  // línea; si no sale, respaldo en pending_comments.json, que se reenvía al volver la red.
  async saveComments ({ id, comments } = {}) {
    const occurrenceId = id ?? this.current?.id
    if (occurrenceId == null) return { ok: false }
    const text = comments ?? ''
    if (String(this.current?.id) === String(occurrenceId)) this.current.comments = text

    if (isOfflineStorageReady()) {
      try {
        await offlineOccurrences.saveComments(occurrenceId, text)
        discardPendingComment(occurrenceId).catch(err => console.error(err))
        scheduleSyncNudge()
        return { ok: true, queued: true }
      } catch (err) {
        captureOfflineError(err, { op: 'occurrence.saveComments', occurrence_id: occurrenceId })
      }
    }

    if (navigator.onLine) {
      try {
        const res = await OccurrencesService.saveComments({ id: occurrenceId, comments: text })
        if (res?.result !== 'update operation has not been updated') {
          discardPendingComment(occurrenceId).catch(err => console.error(err))
          return { ok: true }
        }
      } catch (err) {
        console.error(err)
      }
    }

    try {
      await savePendingComment({ occurrenceId, comments: text })
      return { ok: true, pending: true }
    } catch (err) {
      console.error(err)
      Notify.create({ type: 'negative', message: i18n.global.t('validations.occurrence_comment_not_saved') })
      return { ok: false }
    }
  },
  // OVERLAY DE REINGRESO de los comentarios adicionales (F-97): find() reconstruye la ocurrencia desde el
  // servidor, que todavía tiene el texto viejo si el comentario no se ha subido. Primero el outbox (lo más
  // reciente con la base abierta) y si no, el respaldo legacy. Si queda algo en el respaldo, se intenta
  // reenviar ya (best-effort): al reabrir con red no hay evento `online` que lo dispare.
  async overlayPendingComments (occurrenceId) {
    try {
      let pending = isOfflineStorageReady() ? await offlineOccurrences.getPendingComments(occurrenceId) : null
      if (!pending) {
        pending = await getPendingComment(occurrenceId)
        if (pending) replayPendingComments().catch(err => console.error('[overlayPendingComments] replay', err))
      }
      if (!pending || String(this.current?.id) !== String(occurrenceId)) return
      this.current.comments = pending.comments
      offlineDbg('offline', `overlay re-entrada: comentarios adicionales pendientes en occ ${occurrenceId}`)
    } catch (e) {
      offlineDbgWarn('offline', 'overlay de comentarios adicionales pendientes falló (no crítico)', e)
    }
  },
  signatureRoles (payload) {
    // OFFLINE: el catálogo de roles de firma adicional se llena por red
    // (POST roles/). Sin conexión llegaba vacío y la validación de cierre bloqueaba el finalizar
    // offline (ISS-843). Si hay red, se cachea la respuesta en la tabla `meta`
    // (SQLite) para tenerla disponible offline; si NO hay red o el fetch falla, se lee del cache.
    return resolveSignatureRolesWithCache(payload).then(data => {
      this.signatureroles = data
      return data
    }).catch(err => console.error(err))
  },
  setUncompletedTasks () {
    this.uncompletedTasks = (this.tasksEditing || []).some(isTaskIncomplete)
  },
  async deleteSignature (signature) {
    let id = signature.id ?? null
    if (id == null && signature._pendingSignatureRef) {
      const { removed, serverId } = await removePendingSignature(signature._pendingSignatureRef)
      if (removed) {
        this.removeSignatureFromList(signature)
        return { deleted: true }
      }
      id = serverId
    }
    if (id == null && signature._pendingClientUuid) {
      id = await offlineOccurrences.signatureServerId(signature._pendingClientUuid)
    }
    if (id == null) return { deleted: false }

    await OccurrencesService.deleteSignature(id)
    this.removeSignatureFromList(signature)
    return { deleted: true }
  },
  removeSignatureFromList (signature) {
    const index = this.current.signatures.indexOf(signature)
    if (index !== -1) this.current.signatures.splice(index, 1)
  },
  async backupUnsentSignatures (signatures) {
    try {
      const refs = await savePendingSignatures({
        occurrenceId: this.current.id,
        formAssignmentId: this.current.form_assignment_id,
        signatures: signatures.map(signatureForServer)
      })
      signatures.forEach((sig, index) => {
        sig.is_new = false
        sig._pendingSignatureRef = refs[index]
      })
    } catch (err) {
      console.error(err)
      Notify.create({ type: 'negative', message: i18n.global.t('validations.occurrence_signature_not_saved') })
    }
  },
  async overlayPendingSignatureBackup (occurrenceId) {
    try {
      const pending = await listPendingSignatures(occurrenceId)
      if (!pending.length || String(this.current?.id) !== String(occurrenceId)) return
      if (!Array.isArray(this.current.signatures)) this.current.signatures = []
      for (const { ref, signature } of pending) {
        if (this.current.signatures.some(s => s._pendingSignatureRef === ref)) continue
        this.current.signatures.push({ ...signature, is_new: false, _pendingSignatureRef: ref })
      }
    } catch (err) {
      console.error(err)
    }
  },
  getLastAnswer (payload) {
    const promise = OccurrencesService.getLastAnswer(payload)

    promise.catch(err => console.error(err))

    return promise
  },
  updateUnansweredItems (items) {
    this.unansweredItems = items
  },
  updateGroupsWithUnansweredItems (groups) {
    this.groupsWithUnansweredItems = new Set(groups)
  },
  // Descarga del PDF de una ocurrencia. La generacion es async en backend (fuera
  // del request, sin timeout): arrancamos la exportacion, hacemos polling hasta
  // que quede lista y aplicamos el mismo umbral que el backend
  // (threshold_bytes = config mail.pdf_attach_max_bytes, 8MB por defecto):
  //   - <= umbral -> descarga directa en el navegador.
  //   - >  umbral -> notificacion con link de descarga (no forzamos un archivo
  //     pesado a ciegas).
  async downloadPdf (payload) {
    // TKT-2182: el PDF lo arma el BACKEND leyendo la occurrence. Si el cierre encolado todavía no
    // drenó, el server la ve abierta y devuelve un PDF con "Sin Finalizar" y calificación 0 — el
    // mismo documento equivocado que el correo. Se espera al cierre antes de arrancar la exportación.
    const cierre = await this.waitForPendingCloseDrained(payload.occurrence_id)
    if (!cierre.drained) {
      Notify.create({
        type: 'warning',
        icon: 'sync',
        message: i18n.global.t('sync.occurrencePendingForDocument'),
        timeout: 8000
      })
      return
    }
    const respuestas = await this.waitForPendingAnswersDrained(payload.occurrence_id)
    if (!respuestas.drained) {
      Notify.create({
        type: 'warning',
        icon: 'sync',
        message: i18n.global.t('sync.occurrencePendingForDocument'),
        timeout: 8000
      })
      return
    }
    const avisarPdf = shouldConfirmUnfinished({
      hadPending: cierre.hadPending, current: this.current, occurrenceId: payload.occurrence_id
    })
    if (avisarPdf && !(await this.confirmSendUnfinished())) return

    // Aviso NO bloqueante: el usuario puede seguir usando la app mientras el
    // backend genera el PDF (async, sin timeout del request).
    const generatingNotify = Notify.create({
      type: 'ongoing',
      message: i18n.global.t('occurrences.pdf.generating')
    })

    // Polling holgado para formularios grandes: hasta ~5 min (el job de backend
    // tiene timeout de generacion de 300s). Intervalo de 3s para no martillar.
    const POLL_INTERVAL = 3000
    const POLL_MAX_ATTEMPTS = 100

    // Espera POLL_INTERVAL, pero si el usuario vuelve a la pestana/app (deja de
    // estar oculta) corta la espera para pollear de inmediato. En segundo plano
    // los timers se estrangulan; esto hace que al volver se resuelva rapido.
    const waitOrResume = (ms) => new Promise(resolve => {
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        document.removeEventListener('visibilitychange', onVisible)
        resolve()
      }
      const onVisible = () => { if (!document.hidden) done() }
      document.addEventListener('visibilitychange', onVisible)
      setTimeout(done, ms)
    })

    try {
      const { export_id: exportId } = await OccurrencesService.startPdfExport(payload.occurrence_id)

      let status = null
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        await waitOrResume(POLL_INTERVAL)
        status = await OccurrencesService.getPdfExportStatus(exportId)

        if (status.status === 'ready') break
        if (status.status === 'failed') throw new Error('PDF export failed')
      }

      generatingNotify()

      if (status?.status !== 'ready') {
        Notify.create({
          type: 'info',
          icon: 'schedule',
          message: i18n.global.t('occurrences.pdf.taking_long'),
          timeout: 8000
        })
        return
      }

      if (status.size_bytes > status.threshold_bytes) {
        // Grande: el backend ya emitio la notificacion al panel; el usuario la
        // toca para descargar de una vez. Aqui solo confirmamos.
        Notify.create({
          type: 'info',
          icon: 'notifications',
          message: i18n.global.t('occurrences.pdf.ready_in_notifications'),
          timeout: 6000
        })
        return
      }

      // Chico: descarga directa reutilizando el guardado nativo probado. Se trae
      // el blob por el mismo dominio (stream por token, sin CORS) y se pasa a
      // saveToDevice en Capacitor (guarda, abre y ofrece compartir) o saveToDesktop
      // en web (anchor de descarga).
      const filename = this.pdfFilename(payload)
      const data = await OccurrencesService.fetchPdfBlob(`${status.download_url}/download`)
      const blob = new Blob([data], { type: 'application/pdf' })

      if (Capacitor.isNativePlatform()) {
        utils.saveToDevice(filename, blob, 'application/pdf')
      } else {
        utils.saveToDesktop(filename, blob)
      }

      Notify.create({
        type: 'positive',
        message: i18n.global.t('occurrences.pdf.downloaded')
      })
    } catch (err) {
      generatingNotify()
      Notify.create({
        type: 'negative',
        message: i18n.global.t('occurrences.pdf.generate_error')
      })
      console.error('Error al generar PDF', err)
    }
  },
  pdfFilename (payload) {
    return pdfFilename(payload)
  },
  setLoading (loading) {
    this.loading = loading
  },
  setLogicFag (payload) {
    this.logicFag = payload
  },
  // Reemplaza el arreglo de items para que los getters que dependen de el (currentFormGroups) se
  // recalculen cuando cambia algo que no leen reactivamente (ej. las fotos rotas que se quitan de addons).
  refreshCurrentFormItems () {
    if (!this.current?.form) return
    this.current.form.items = [...this.current.form.items]
  },
  setCurrentFormItemOptions (itemId, options) {
    const item = this.current?.form?.items.find(formItem => formItem.id === itemId)
    if (item) item.options = options
  },
  async setCurrentOccurrenceAnswer (answer) {
    // Aquí ya NO se suben imágenes.
    // La subida de item tipo foto ocurre únicamente en:
    // submitOccurrenceUpdate()

    const answerIndex = this.current.occurrence_answers.findIndex(
      _answer => _answer.item_id === answer.item_id
    )

    if (answerIndex > -1) {
      this.current.occurrence_answers.splice(answerIndex, 1)
      this.current.occurrence_answers.push(answer)
    } else {
      this.current.occurrence_answers.push(answer)
    }
  },
  restoreCurrentOccurrenceAnswer (itemId, previousAnswer) {
    const answerIndex = this.current.occurrence_answers.findIndex(_answer => _answer.item_id === itemId)
    if (answerIndex > -1) this.current.occurrence_answers.splice(answerIndex, 1)
    if (previousAnswer) this.current.occurrence_answers.push(previousAnswer)
  },
  setCurrentOccurrenceExtraProperty (answer) {
    const answerIndex = this.current.occurrence_answers.findIndex(_answer => _answer.item_id === answer.item_id)
    if (answerIndex > -1) {
      this.current.occurrence_answers[answerIndex].extra_properties = answer.extra_properties
    } else {
      this.current.occurrence_answers.push(answer)
    }
  },
  readNativeImageAsBase64 (uri) {
    return readImageFromFileSystem(uri)
  },
  readNativeImageAsBlob (uri) {
    return readImageAsBlob(uri)
  },
  async setCurrentOccurrenceAnswerFromNativeUri ({ item_id: itemId, localPath, extra_properties: extraProperties }) {
    const blob = await readImageAsBlob(localPath)
    if (blob) {
      this.setCurrentOccurrenceAnswer({
        item_id: itemId,
        value: URL.createObjectURL(blob),
        localPath,
        extra_properties: extraProperties
      })
    }
  },
  persistLegacyOfflineAddon (offlineAddon, { photoQualityProfile } = {}) {
    return persistLegacyAddon(offlineAddon, {
      photoQualityProfile,
      processPhoto: (source, options) => utils.processPhoto(source, options),
      onPersisted: addon => this.setAddons(addon)
    })
  },
  async clearLegacyOfflineAddon (offlineAddon) {
    return clearLegacyAddon(offlineAddon)
  },
  saveLegacyOfflineDocument ({ occurrenceId, itemId, file, filename }) {
    return saveLegacyDocument({ occurrenceId, itemId, file, filename })
  },
  clearLegacyOfflineDocument ({ occurrenceId, itemId }) {
    return clearLegacyDocument({ occurrenceId, itemId })
  },
  async replayPendingOfflineCloses () {
    await sendPendingSignatures(this.current)
    // Los comentarios van antes que el cierre (F-97), igual que las firmas.
    await replayPendingComments().catch(err => console.error(err))
    return replayPendingCloses()
  },
  hasDeferredCloseFor (occurrenceId) {
    return hasDeferredClose(occurrenceId)
  },
  async closeOccurrenceOffline ({ occurrenceId, payload, tasksEditing, closeOnline, onError }) {
    const outcome = await closeWithFallback({
      tryOffline: () => offlineOccurrences.closeOccurrence(String(occurrenceId), { payload, tasksEditing }),
      isReallyOnline: isReallyOnlineUi,
      closeOnline,
      onFallback: (e) => captureOfflineError(e, { op: 'occurrence.close.v2.fallback' }),
      onError: (e) => {
        captureOfflineError(e, { op: 'occurrence.close.v2' })
        if (onError) onError(e)
      }
    })
    if (outcome === 'offline') {
      this.markOccurrenceClosedLocally(occurrenceId)
      offlineSyncNow({ force: true }).catch(() => {})
    }
    return outcome
  },
  deferOccurrenceClose ({ occurrenceId, payload, tasksEditing, closeOnline, onError }) {
    return closeWithFallback({
      tryOffline: () => savePendingClose({ occurrenceId: String(occurrenceId), payload, tasksEditing }),
      isReallyOnline: isReallyOnlineUi,
      closeOnline,
      onFallback: (e) => captureOfflineError(e, { op: 'occurrence.close.deferred.fallback' }),
      onError: (e) => {
        captureOfflineError(e, { op: 'occurrence.close.deferred' })
        if (onError) onError(e)
      }
    })
  },
  remapTasksEditingServerIds (tasksEditing) {
    return offlineTasks.remapTasksEditingServerIds(tasksEditing)
  },
  async saveEditingTasksForClose ({ occurrenceId, tasks, remapServerIds }) {
    let tasksToSave = tasks
    if (remapServerIds) {
      try {
        tasksToSave = await this.remapTasksEditingServerIds(tasksToSave)
      } catch (error) {
        console.error(error)
      }
    }
    return OccurrencesService.saveEditingTasks(occurrenceId, tasksToSave)
  },
  async listRealPendingOfflineAnswers (occurrenceId) {
    return listRealPendingLegacyAnswers(occurrenceId)
  },
  clearOfflineAnswersForOccurrence (occurrenceId) {
    return OfflineStorage.clearForOccurrence(occurrenceId)
  },
  async syncPendingAnswersAfterLoad ({ id, isOnline, isReplaced, onSyncStart }) {
    // Revisar si hay respuestas offline (SOLO pendientes reales)
    const realPending = await discardStaleLegacyAnswers(id)

    //  No hay nada REAL que sincronizar
    if (realPending.length === 0) {
      return { status: 'none' }
    }

    // Solo sincronizar si hay conexión
    if (!isOnline() || isReplaced()) {
      return { status: 'skipped' }
    }

    onSyncStart?.()

    // Delay corto antes de sincronizar
    await new Promise(resolve => setTimeout(resolve, 500))
    if (isReplaced()) return { status: 'replaced' }

    const syncResult = await this.saveOfflineAnswers()

    // Recargar auditoría después de sincronizar
    if (isReplaced()) return { status: 'replaced' }
    await this.find(id)

    return { status: 'synced', result: syncResult }
  },
  async createTaskOffline ({ task, telemetryOp = 'task.createFromForms.v2', assignment = null, optimistic = true }) {
    const clientUuid = newId()
    const fullTask = { ...task, client_uuid: clientUuid }
    const pictureUuids = (task.pictures || []).filter(Boolean).map(() => newId())
    if (optimistic) {
      this.addOptimisticTask({
        ...fullTask,
        picture_uuids: pictureUuids,
        ...(assignment && {
          assigned_to_id: assignment.userId,
          assigned_to_name: assignment.userName,
          pending_assignment: assignment.pending
        })
      })
    }
    try {
      await offlineTasks.createTask(fullTask, { pictureUuids })
      if (assignment?.pending) await this.markTaskAssignmentPending(clientUuid)
      scheduleSyncNudge()
    } catch (e) {
      captureOfflineError(e, { op: telemetryOp })
      throw e
    }
    return clientUuid
  },
  async editTaskOffline ({ taskId, dataChanges, bodyChanges }) {
    try {
      await offlineTasks.editTask(taskClientUuid(taskId), dataChanges, bodyChanges)
      this.updateOptimisticTaskFields(taskId, { ...dataChanges })
      scheduleSyncNudge()
    } catch (e) {
      captureOfflineError(e, { op: 'task.updateFields.v2' })
    }
  },
  async deleteTaskOffline (taskId) {
    try {
      await offlineTasks.deleteTask(taskClientUuid(taskId))
      await this.clearTaskAssignmentPending(taskId)
      this.removeOptimisticTask(taskId)
      scheduleSyncNudge()
    } catch (e) {
      captureOfflineError(e, { op: 'task.delete.v2' })
    }
  },
  saveLinkedTaskFields ({ task, hierarchy, roleId, userId, title }) {
    const record = {
      taskId: task.id,
      priority: task.priority ? task.priority : null,
      end_date: task.end_date ? task.end_date : null,
      tag: task.category,
      tag2: task.category2,
      enforce_evidence: task.enforce_evidence,
      area_id: hierarchy.area_id,
      hierarchy_site_id: hierarchy.hierarchy_site_id,
      hierarchy_subcategory_id: hierarchy.hierarchy_subcategory_id
    }
    if (userId !== null) {
      record.role = roleId
      record.users = userId
    }
    if (title) record.title = title
    if (!isOfflineCaptureReady()) {
      return TasksService.updateFilds(record)
    }

    const bodyChanges = { ...record }
    delete bodyChanges.taskId
    const dataChanges = {
      priority: task.priority ?? null,
      end_date: task.end_date ?? null,
      category: task.category ?? null,
      category2: task.category2 ?? null,
      enforce_evidence: task.enforce_evidence ?? null,
      area_id: hierarchy.area_id ?? null,
      hierarchy_site_id: hierarchy.hierarchy_site_id ?? null,
      hierarchy_subcategory_id: hierarchy.hierarchy_subcategory_id ?? null
    }
    if (userId !== null) {
      dataChanges.role_id = roleId
      dataChanges.assigned_to_id = userId
    }
    if (title) dataChanges.title = title
    return this.editTaskOffline({ taskId: task.id, dataChanges, bodyChanges })
  },
  resolveImageBase64 (image) {
    return resolveSavedImage(image, { readNativeImage: uri => this.readNativeImageAsBase64(uri) })
  },
  async resolveTaskPhotoServerIds ({ taskId, picture }) {
    if (!picture) return null
    if (isServerId(picture.id) && isServerId(taskId)) {
      return { taskId: Number(taskId), answerId: Number(picture.id) }
    }
    return offlineTasks.resolveSyncedTaskPhoto({ taskId, picture })
  },
  waitForTaskPhotoSync ({ taskId, picture }) {
    return offlineTasks.waitForSyncedTaskPhoto({ taskId, picture, kick: () => offlineSyncNow() })
  },
  async addTaskPictureOffline ({ taskId, photoBase64 }) {
    try {
      const evUuid = await offlineTasks.addTaskEvidence({
        task_client_uuid: taskClientUuid(taskId),
        kind: 'task_picture',
        photoBase64
      })
      scheduleSyncNudge()
      return evUuid
    } catch (e) {
      captureOfflineError(e, { op: 'task.uploadImage.v2' })
      return null
    }
  },
  async saveItemAnswer ({ item, itemId, value, iaResult = null, getIsOnline }) {
    this.setLogicFag({ id: itemId, value })

    if (this.unansweredItems.length > 0) {
      const updatedUnansweredItems = this.unansweredItems.filter(unanswered => unanswered !== itemId)
      this.updateUnansweredItems(updatedUnansweredItems)
    }

    if (!this.current) return

    const isPhotoValue = typeof value === 'string' && value.startsWith('file://')
    const previousAnswer = this.current.occurrence_answers.find(answer => answer.item_id === itemId)
    const data = {
      item_id: itemId,
      value,
      localPath: isPhotoValue ? value : null,
      extra_properties: this.itemExtraProperties(item.id)
    }
    if (iaResult !== null) {
      data.ia_result = iaResult.status
      data.ia_image_evaluation_justification = iaResult.justification
    }

    this.setCurrentOccurrenceAnswer(data).catch(err => captureOfflineError(err, { op: 'occurrence.setCurrentAnswer', item_id: itemId }))

    const isBase64Photo = typeof value === 'string' && value.startsWith('data:image')
    const isNativeFilePhotoPath = typeof value === 'string' &&
      (value.startsWith('file://') || value.startsWith('content://'))
    const isBlobPhoto = typeof value === 'string' && value.startsWith('blob:')

    let photoBase64 = isBase64Photo ? value : undefined
    if (isNativeFilePhotoPath) {
      try {
        const b64 = await readImageFromFileSystem(value)
        if (typeof b64 === 'string' && b64.startsWith('data:image')) {
          photoBase64 = b64
          offlineDbg('photo', `item ${itemId}: foto Capacitor file:// -> base64 OK -> offline`)
        } else {
          offlineDbgWarn('photo', `item ${itemId}: readImageFromFileSystem no devolvió data:image (file=${value})`)
        }
      } catch (e) {
        offlineDbgWarn('photo', `item ${itemId}: NO se pudo leer la foto Capacitor a base64 -> fallback legacy`, e)
      }
    } else if (isBlobPhoto) {
      try {
        const res = await fetch(value)
        const blobData = await res.blob()
        photoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blobData)
        })
        offlineDbg('photo', `item ${itemId}: foto blob: -> base64 OK -> offline`)
      } catch (e) {
        offlineDbgWarn('photo', `item ${itemId}: NO se pudo convertir foto blob: a base64 -> fallback legacy`, e)
      }
    }
    const nativePhotoUnresolved = (isNativeFilePhotoPath || isBlobPhoto) && !photoBase64

    if (nativePhotoUnresolved) {
      recordBreadcrumb('form.capture', `foto ilegible, no se guarda (item ${itemId})`, 'warning', {
        item_id: itemId,
        occurrence_id: this.current.id
      })
      offlineDbgWarn('photo', `item ${itemId}: foto no convertible a base64; se descarta y se pide tomarla de nuevo`)
      this.restoreCurrentOccurrenceAnswer(itemId, previousAnswer)
      reportPhotoCaptureFailed({ occurrenceId: this.current.id.toString(), itemId })
      Notify.create({ type: 'negative', message: i18n.global.t('sync.photoCaptureFailed') })
      if (this.current.form.assignment.auto_ap) {
        this.createAutoActionPlanTask({ item, itemId, getIsOnline })
          .catch(err => captureOfflineError(err, { op: 'occurrence.autoActionPlanTask', item_id: itemId }))
      }
      return
    }

    const hasPhoto = !!photoBase64
    recordBreadcrumb('form.capture', `respuesta guardada v2 (item ${itemId})`, 'info', {
      item_id: itemId,
      occurrence_id: this.current.id
    })
    try {
      await offlineOccurrences.saveAnswer({
        occurrence_id: this.current.id.toString(),
        item_id: itemId,
        value: hasPhoto ? null : value,
        current_points: this.itemObtainedPoints(item.id),
        total_points: this.itemPossiblePoints(item.id),
        extra_properties: this.itemExtraProperties(item.id),
        photoBase64
      })
      scheduleSyncNudge()
    } catch (err) {
      captureOfflineError(err, { op: 'occurrence.saveAnswer.v2', item_id: itemId })
      const fallbackAnswer = {
        occurrence_id: this.current.id.toString(),
        form_assignment_location_occurrence_id: this.current.id.toString(),
        item_id: itemId,
        value: hasPhoto ? photoBase64 : value,
        extra_properties: this.itemExtraProperties(item.id),
        current_points: this.itemObtainedPoints(item.id),
        total_points: this.itemPossiblePoints(item.id),
        created_at: moment.utc(moment()).format('YYYY-MM-DD HH:mm:ss'),
        updated_at: moment.utc(moment()).format('YYYY-MM-DD HH:mm:ss')
      }
      try {
        await this.saveLegacyOfflineAnswer(fallbackAnswer, { allowPhotoReplace: hasPhoto })
      } catch (fallbackErr) {
        console.error(`[saveItemAnswer] fallback legacy falló (item ${itemId}):`, fallbackErr)
        if (hasPhoto) reportPhotoCaptureFailed({ occurrenceId: this.current.id.toString(), itemId })
        Notify.create({
          type: 'negative',
          message: i18n.global.t(hasPhoto ? 'sync.photoCaptureFailed' : 'sync.saveAnswerFailed')
        })
      }
    }

    if (this.current.form.assignment.auto_ap) {
      this.createAutoActionPlanTask({ item, itemId, getIsOnline })
        .catch(err => captureOfflineError(err, { op: 'occurrence.autoActionPlanTask', item_id: itemId }))
    }
  },
  async saveItemExtraProperties ({ item, extraProperties, getAnswer }) {
    if (!this.current) return

    const offlineAnswer = {
      occurrence_id: this.current.id.toString(),
      form_assignment_location_occurrence_id: this.current.id.toString(),
      item_id: item.id,
      value: getAnswer(),
      extra_properties: extraProperties,
      current_points: this.itemObtainedPoints(item.id),
      total_points: this.itemPossiblePoints(item.id),
      created_at: moment.utc(moment()).format('YYYY-MM-DD HH:mm:ss'),
      updated_at: moment.utc(moment()).format('YYYY-MM-DD HH:mm:ss')
    }
    this.setCurrentOccurrenceExtraProperty({ item_id: item.id, extra_properties: extraProperties })

    recordBreadcrumb('form.capture', `extra-property guardada v2 (item ${item.id})`, 'info', {
      item_id: item.id,
      occurrence_id: this.current.id
    })
    try {
      await offlineOccurrences.saveAnswer({
        occurrence_id: this.current.id.toString(),
        item_id: item.id,
        value: getAnswer(),
        current_points: this.itemObtainedPoints(item.id),
        total_points: this.itemPossiblePoints(item.id),
        extra_properties: extraProperties
      })
      scheduleSyncNudge()
    } catch (err) {
      captureOfflineError(err, { op: 'occurrence.setExtraProperty.v2', item_id: item.id })
      try {
        await this.saveLegacyOfflineAnswer(offlineAnswer, { onlyExtraProperties: true })
      } catch (fallbackErr) {
        captureOfflineError(fallbackErr, { op: 'occurrence.setExtraProperty.fallbackLegacy', item_id: item.id })
        console.error(`[saveItemExtraProperties] fallback legacy (extra-property item ${item.id}) falló:`, fallbackErr)
        Notify.create({ type: 'negative', message: i18n.global.t('sync.saveAnswerFailed') })
      }
    }
  },
  async submitOccurrenceUpdate ({ occurrence, syncItemId, item }) {
    const { id, occurrence_answers: occurrenceAnswers } = occurrence

    const rawPhoto = await findLegacyRawPhoto(id, syncItemId)

    if (rawPhoto && Capacitor.isNativePlatform() && navigator.onLine) {
      try {
        const rawBlob = await readImageAsBlob(rawPhoto.localPath)
        if (!rawBlob) {
          throw new Error('readImageAsBlob no devolvió un Blob')
        }
        const { blob, url } = await utils.processPhoto(rawBlob, {
          maxWidth: 1400,
          profile: await resolvePhotoQualityProfile()
        })
        URL.revokeObjectURL(url)

        const formData = new FormData()
        const imageRef = rawPhoto.image_ref || `img-${Date.now()}-${rawPhoto.item_id}`

        formData.append('answers[0][occurrence_id]', String(id))
        formData.append('answers[0][form_assignment_location_occurrence_id]', String(id))
        formData.append('answers[0][item_id]', String(rawPhoto.item_id))
        formData.append('answers[0][image_ref]', imageRef)
        formData.append('answers[0][value]', imageRef)
        formData.append('answers[0][file]', blob, `${imageRef}.jpg`)

        return await withTimeout(
          OccurrencesService.updateFormData(id, this.current.form_assignment_id, formData),
          10000
        )
      } catch (e) {
        console.error('Error subiendo imagen:', e)
        throw e
      }
    }

    const currentAnswer = occurrenceAnswers?.find(a => Number(a.item_id) === Number(syncItemId))
    if (
      !Capacitor.isNativePlatform() &&
      typeof currentAnswer?.value === 'string' &&
      (currentAnswer.value.startsWith('data:image') || currentAnswer.value.startsWith('blob:'))
    ) {
      try {
        const res = await fetch(currentAnswer.value)
        if (!res.ok) throw new Error(`fetch base64 falló (${res.status})`)
        const blob = await res.blob()
        if (!blob || blob.size === 0) throw new Error('Blob vacío al convertir base64')

        const imageRef = `img-${Date.now()}-${syncItemId}`
        const formData = new FormData()
        formData.append('answers[0][occurrence_id]', String(id))
        formData.append('answers[0][form_assignment_location_occurrence_id]', String(id))
        formData.append('answers[0][item_id]', String(syncItemId))
        formData.append('answers[0][image_ref]', imageRef)
        formData.append('answers[0][value]', imageRef)
        formData.append('answers[0][file]', blob, `${imageRef}.jpg`)

        const extraProps = serializeExtraProperties(currentAnswer.extra_properties)
        if (extraProps !== null) {
          formData.append('answers[0][extra_properties]', extraProps)
        }

        return await withTimeout(
          OccurrencesService.updateFormData(id, this.current.form_assignment_id, formData),
          10000
        )
      } catch (e) {
        console.error('[B.9c] Error subiendo foto web vía FormData:', e)
        throw e
      }
    }

    const preAnswers = buildPreAnswers(
      occurrenceAnswers?.filter(a => Number(a.item_id) === Number(syncItemId)),
      {
        item,
        obtainedPoints: () => this.itemObtainedPoints(item.id),
        possiblePoints: () => this.itemPossiblePoints(item.id)
      }
    )

    const newSignatures = this.current.signatures?.filter(s => s.is_new) || []

    const updatePayload = {
      answers: preAnswers,
      form_assignment_id: this.current.form_assignment_id,
      signatures: newSignatures,
      score: this.currentScore,
      self_evaluation_result: this.currentSelfEvaluationResult,
      id
    }

    if (this.extraPropertiesSumPayload !== null) {
      updatePayload.extra_properties_sum = this.extraPropertiesSumPayload
    }

    if (newSignatures.length && isOfflineStorageReady()) {
      const queued = []
      try {
        for (const sig of newSignatures) {
          sig._pendingClientUuid = await offlineOccurrences.addSignature({
            occurrence_id: id,
            form_assignment_id: this.current.form_assignment_id,
            role: sig.audited_rol_name || sig.role || null,
            signatureDataUrl: sig.signature_path,
            signature_kind: sig.is_auditor ? 'auditor' : (sig.is_audited ? 'audited' : 'other'),
            signed_by: sig.signed_by,
            signed_name: sig.signed_name,
            is_auditor: sig.is_auditor,
            is_audited: sig.is_audited,
            audited_rol_name: sig.audited_rol_name,
            audited_name: sig.audited_name,
            payload: { ...updatePayload, signatures: [sig] }
          })
          sig.is_new = false
          queued.push(sig)
        }
      } catch (err) {
        captureOfflineError(err, { op: 'occurrence.addSignature.v2', occurrence_id: id })
      }
      if (queued.length) offlineSyncNow().catch(() => {})
      if (queued.length === newSignatures.length) return { ok: true }
      updatePayload.signatures = newSignatures.filter(sig => !queued.includes(sig))
    }

    const result = await this.update(updatePayload)

    if (result?.ok === false) {
      if (updatePayload.signatures.length) await this.backupUnsentSignatures(updatePayload.signatures)
      return result
    }

    const savedSignatures = result?.data?.data?.signatures || []
    updatePayload.signatures.forEach((sig, index) => {
      sig.is_new = false
      if (savedSignatures[index]?.id != null) sig.id = savedSignatures[index].id
    })

    return result
  },
  saveLegacyOfflineAnswer (answer, opts) {
    return writeLegacyAnswer(answer, opts)
  },
  async createAutoActionPlanTask ({ item, itemId, getIsOnline }) {
    if (!(this.itemObtainedPoints(item.id) < this.itemPossiblePoints(item.id))) return

    const occurrence = this.current
    const title = occurrence.form.items.find(element => element.id === itemId).name
    const roleId = this.actionPlanRoleFor({ auto: true })
    const locationId = occurrence.location.id
    const provisionalAssignee = occurrence.audited_user !== null ? occurrence.audited_user.id : await currentUserId()
    const assignee = await this.resolveInitialAssignee({ roleId, locationId, online: getIsOnline() !== false })
    const isStillCurrent = String(this.current?.id) === String(occurrence.id)
    const newTask = {
      ocurrence_id: occurrence.id,
      form_id: occurrence.form.id,
      role_id: roleId,
      location_id: locationId,
      assigned_to: assignee.userId ?? provisionalAssignee,
      state: 'editing',
      item_id: itemId,
      title,
      pictures: null,
      auto_action_plan: true
    }

    if (isOfflineCaptureReady()) {
      try {
        await this.createTaskOffline({
          task: { ...newTask, ...this.actionPlanDefaults() },
          telemetryOp: 'task.autoCreate.v2',
          assignment: { userId: assignee.userId, userName: assignee.userName, pending: assignee.pending },
          optimistic: isStillCurrent
        })
      } catch {
        Notify.create({
          type: 'negative',
          message: i18n.global.t('sync.taskAutoCreateFailed'),
          timeout: 4000
        })
      }
      return
    }

    if (!getIsOnline()) {
      Notify.create({
        type: 'negative',
        message: i18n.global.t('sync.taskAutoCreateFailed'),
        timeout: 4000
      })
      return
    }

    try {
      await TasksService.createTaskEditing(newTask)
    } catch (err) {
      console.error(err)
      return
    }
    if (String(this.current?.id) !== String(occurrence.id)) return
    this.reloadTasks(occurrence.id).catch(e => {
      console.error('Error + ' + e)
    })
  }
}
